/**
 * A paused planner. Hosts yield between short steps; no learner state changes.
 */
export class AnkiDeckPlanner {
    static __wrap(ptr) {
        const obj = Object.create(AnkiDeckPlanner.prototype);
        obj.__wbg_ptr = ptr;
        AnkiDeckPlannerFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        AnkiDeckPlannerFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_ankideckplanner_free(ptr, 0);
    }
    /**
     * Finish only after `step` reports done; this never runs remaining work.
     * @returns {AnkiDeckPlan}
     */
    finish() {
        const ret = wasm.ankideckplanner_finish(this.__wbg_ptr);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return takeFromExternrefTable0(ret[0]);
    }
    /**
     * Index a small batch, answer one simulated challenge, or try one pending
     * gram. A simulated day is deliberately not an atomic unit of work.
     * @returns {AnkiDeckStep}
     */
    step() {
        const ret = wasm.ankideckplanner_step(this.__wbg_ptr);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return takeFromExternrefTable0(ret[0]);
    }
}
if (Symbol.dispose) AnkiDeckPlanner.prototype[Symbol.dispose] = AnkiDeckPlanner.prototype.free;

/**
 * Audio bytes plus a sidecar identifying the voice actor, when the clip
 * came from a human recording rather than TTS.
 */
export class AudioResult {
    static __wrap(ptr) {
        const obj = Object.create(AudioResult.prototype);
        obj.__wbg_ptr = ptr;
        AudioResultFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        AudioResultFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_audioresult_free(ptr, 0);
    }
    /**
     * @returns {Uint8Array}
     */
    get bytes() {
        const ret = wasm.audioresult_bytes(this.__wbg_ptr);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return takeFromExternrefTable0(ret[0]);
    }
    /**
     * Container-derived MIME type, including pronunciation-guide M4A audio.
     * @returns {string}
     */
    get mime_type() {
        let deferred2_0;
        let deferred2_1;
        try {
            const ret = wasm.audioresult_mime_type(this.__wbg_ptr);
            var ptr1 = ret[0];
            var len1 = ret[1];
            if (ret[3]) {
                ptr1 = 0; len1 = 0;
                throw takeFromExternrefTable0(ret[2]);
            }
            deferred2_0 = ptr1;
            deferred2_1 = len1;
            return getStringFromWasm0(ptr1, len1);
        } finally {
            wasm.__wbindgen_free(deferred2_0, deferred2_1, 1);
        }
    }
    /**
     * The voice actor behind the clip when it's human-recorded, else `None`.
     * @returns {(VoiceActorInfo | undefined)}
     */
    get voice_actor() {
        const ret = wasm.audioresult_voice_actor(this.__wbg_ptr);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return takeFromExternrefTable0(ret[0]);
    }
}
if (Symbol.dispose) AudioResult.prototype[Symbol.dispose] = AudioResult.prototype.free;

/**
 * A sentence's movie clip: lo.mp4 bytes plus the timing metadata the video
 * player uses (total length and the critical window containing the sentence
 * itself, for picking a poster frame).
 */
export class ClipResult {
    static __wrap(ptr) {
        const obj = Object.create(ClipResult.prototype);
        obj.__wbg_ptr = ptr;
        ClipResultFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        ClipResultFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_clipresult_free(ptr, 0);
    }
    /**
     * @returns {Uint8Array}
     */
    get bytes() {
        const ret = wasm.clipresult_bytes(this.__wbg_ptr);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return takeFromExternrefTable0(ret[0]);
    }
    /**
     * @returns {bigint}
     */
    get critical_end_ms() {
        const ret = wasm.clipresult_critical_end_ms(this.__wbg_ptr);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return BigInt.asUintN(64, ret[0]);
    }
    /**
     * @returns {bigint}
     */
    get critical_start_ms() {
        const ret = wasm.clipresult_critical_start_ms(this.__wbg_ptr);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return BigInt.asUintN(64, ret[0]);
    }
    /**
     * @returns {bigint}
     */
    get duration_ms() {
        const ret = wasm.clipresult_duration_ms(this.__wbg_ptr);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return BigInt.asUintN(64, ret[0]);
    }
    /**
     * IMDb id of the film this clip was cut from (the first segment of the
     * clip id), for showing the movie's poster next to the video.
     * @returns {string}
     */
    get movie_id() {
        let deferred2_0;
        let deferred2_1;
        try {
            const ret = wasm.clipresult_movie_id(this.__wbg_ptr);
            var ptr1 = ret[0];
            var len1 = ret[1];
            if (ret[3]) {
                ptr1 = 0; len1 = 0;
                throw takeFromExternrefTable0(ret[2]);
            }
            deferred2_0 = ptr1;
            deferred2_1 = len1;
            return getStringFromWasm0(ptr1, len1);
        } finally {
            wasm.__wbindgen_free(deferred2_0, deferred2_1, 1);
        }
    }
    /**
     * Time-synced subtitle cues for the video (clip-relative ms). Empty
     * when unavailable — captions are an enhancement, not a requirement.
     * @returns {Array<ClipSubtitleCue>}
     */
    get subtitles() {
        const ret = wasm.clipresult_subtitles(this.__wbg_ptr);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return takeFromExternrefTable0(ret[0]);
    }
}
if (Symbol.dispose) ClipResult.prototype[Symbol.dispose] = ClipResult.prototype.free;

export class Deck {
    static __wrap(ptr) {
        const obj = Object.create(Deck.prototype);
        obj.__wbg_ptr = ptr;
        DeckFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        DeckFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_deck_free(ptr, 0);
    }
    /**
     * Create a DeckEvent for adding a gram/phrase by its frequency index
     * @param {number} frequency_index
     * @returns {(DeckEvent | undefined)}
     */
    add_gram_by_frequency_index(frequency_index) {
        const ret = wasm.deck_add_gram_by_frequency_index(this.__wbg_ptr, frequency_index);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return takeFromExternrefTable0(ret[0]);
    }
    /**
     * @param {PlacementSession} session
     * @returns {PlacementSession}
     */
    advance_placement_session(session) {
        const ret = wasm.deck_advance_placement_session(this.__wbg_ptr, session);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return takeFromExternrefTable0(ret[0]);
    }
    /**
     * Bytes from the language pack, or optional caption markup. TTS is not
     * in the pack; the host fetches it from the manifest's URL instead.
     * @param {AnkiMediaSource} source
     * @returns {Promise<(Uint8Array | undefined)>}
     */
    anki_bundled_media(source) {
        const ret = wasm.deck_anki_bundled_media(this.__wbg_ptr, source);
        return ret;
    }
    /**
     * Call only after the host has delivered the finished package.
     * @param {AnkiDeckPlan} plan
     * @param {string} deck_id
     * @returns {DeckEvent}
     */
    anki_deck_exported(plan, deck_id) {
        const ptr0 = passStringToWasm0(deck_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.deck_anki_deck_exported(this.__wbg_ptr, plan, ptr0, len0);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return takeFromExternrefTable0(ret[0]);
    }
    /**
     * @param {boolean | null} [starting_fresh]
     * @returns {AnkiExportView}
     */
    anki_export_view(starting_fresh) {
        const ret = wasm.deck_anki_export_view(this.__wbg_ptr, isLikeNone(starting_fresh) ? 0 : addToExternrefTable0(starting_fresh));
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return takeFromExternrefTable0(ret[0]);
    }
    /**
     * A page of assumed-known meanings, most frequent first.
     * @param {number} page
     * @returns {AssumedKnownScreenView}
     */
    assumed_known_view(page) {
        const ret = wasm.deck_assumed_known_view(this.__wbg_ptr, page);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return takeFromExternrefTable0(ret[0]);
    }
    /**
     * @param {Array<ChallengeRequirements>} banned_challenge_types
     * @param {string | null} [access_token]
     * @param {AbortSignal | null} [abort_signal]
     * @returns {Promise<void>}
     */
    cache_challenge_audio(banned_challenge_types, access_token, abort_signal) {
        const ret = wasm.deck_cache_challenge_audio(this.__wbg_ptr, banned_challenge_types, isLikeNone(access_token) ? 0 : addToExternrefTable0(access_token), isLikeNone(abort_signal) ? 0 : addToExternrefTable0(abort_signal));
        return ret;
    }
    /**
     * @param {DailyReviewTarget} daily_review_target
     * @returns {DeckEvent}
     */
    change_daily_review_target(daily_review_target) {
        const ret = wasm.deck_change_daily_review_target(this.__wbg_ptr, daily_review_target);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return takeFromExternrefTable0(ret[0]);
    }
    /**
     * @param {SentenceListSelection | null} [sentence_list]
     * @returns {DeckEvent}
     */
    change_sentence_list(sentence_list) {
        const ret = wasm.deck_change_sentence_list(this.__wbg_ptr, isLikeNone(sentence_list) ? 0 : addToExternrefTable0(sentence_list));
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return takeFromExternrefTable0(ret[0]);
    }
    /**
     * Home's curriculum menu: switch to the list a category opens on.
     * @param {SentenceListCategory} category
     * @returns {DeckEvent}
     */
    choose_curriculum_category(category) {
        const ret = wasm.deck_choose_curriculum_category(this.__wbg_ptr, category);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return takeFromExternrefTable0(ret[0]);
    }
    /**
     * @param {Array<string>} known_words
     * @param {Array<string>} unknown_words
     * @returns {DeckEvent}
     */
    complete_placement_test(known_words, unknown_words) {
        const ret = wasm.deck_complete_placement_test(this.__wbg_ptr, known_words, unknown_words);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return takeFromExternrefTable0(ret[0]);
    }
    /**
     * @param {Array<ChallengeRequirements>} banned
     * @param {number} timestamp_ms
     * @returns {DueWordsScreenView}
     */
    due_words_view(banned, timestamp_ms) {
        const ret = wasm.deck_due_words_view(this.__wbg_ptr, banned, timestamp_ms);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return takeFromExternrefTable0(ret[0]);
    }
    /**
     * @returns {(Accomplishment | undefined)}
     */
    get_accomplishment() {
        const ret = wasm.deck_get_accomplishment(this.__wbg_ptr);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return takeFromExternrefTable0(ret[0]);
    }
    /**
     * Returns all cards as summaries, ordered consistently with get_review_info
     * (due cards first, then future cards, each sorted by due date and card indicator).
     * Includes locked cards — lockup only hides cards from the review queue.
     * @returns {Array<CardSummary>}
     */
    get_all_cards_summary() {
        const ret = wasm.deck_get_all_cards_summary(this.__wbg_ptr);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return takeFromExternrefTable0(ret[0]);
    }
    /**
     * Returns the best movie sentence list: highest RT score among incomplete movies.
     * @returns {(SentenceListSelection | undefined)}
     */
    get_best_movie_sentence_list() {
        const ret = wasm.deck_get_best_movie_sentence_list(this.__wbg_ptr);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return takeFromExternrefTable0(ret[0]);
    }
    /**
     * Returns the best Pimsleur lesson: the first incomplete one (by level/lesson).
     * @returns {(SentenceListSelection | undefined)}
     */
    get_best_pimsleur_sentence_list() {
        const ret = wasm.deck_get_best_pimsleur_sentence_list(this.__wbg_ptr);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return takeFromExternrefTable0(ret[0]);
    }
    /**
     * @param {Array<string>} book_ids
     * @returns {Array<BookMetadata>}
     */
    get_book_metadata(book_ids) {
        const ret = wasm.deck_get_book_metadata(this.__wbg_ptr, book_ids);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return takeFromExternrefTable0(ret[0]);
    }
    /**
     * Essential tier and level derived from overall coverage, including planned cards.
     * @returns {TierInfo}
     */
    get_current_tier() {
        const ret = wasm.deck_get_current_tier(this.__wbg_ptr);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return takeFromExternrefTable0(ret[0]);
    }
    /**
     * Progress for each day of the current week (Monday → Sunday) in the user's local timezone.
     * @returns {Array<DayProgress>}
     */
    get_current_week_progress() {
        const ret = wasm.deck_get_current_week_progress(this.__wbg_ptr);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return takeFromExternrefTable0(ret[0]);
    }
    /**
     * Daily goal target in seconds.
     * @returns {number}
     */
    get_daily_review_target() {
        const ret = wasm.deck_get_daily_review_target(this.__wbg_ptr);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return ret[0] >>> 0;
    }
    /**
     * @returns {DailyReviewTarget}
     */
    get_daily_review_target_setting() {
        const ret = wasm.deck_get_daily_review_target_setting(this.__wbg_ptr);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return takeFromExternrefTable0(ret[0]);
    }
    /**
     * @returns {number}
     */
    get_daily_streak() {
        const ret = wasm.deck_get_daily_streak(this.__wbg_ptr);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return ret[0] >>> 0;
    }
    /**
     * @returns {Array<FrequencyKnowledgePoint>}
     */
    get_frequency_knowledge_chart_data() {
        const ret = wasm.deck_get_frequency_knowledge_chart_data(this.__wbg_ptr);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return takeFromExternrefTable0(ret[0]);
    }
    /**
     * Count distinct words with at least one defined sense.
     * @returns {number}
     */
    get_gram_dictionary_count() {
        const ret = wasm.deck_get_gram_dictionary_count(this.__wbg_ptr);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return ret[0] >>> 0;
    }
    /**
     * @param {string | null | undefined} search_query
     * @param {number} limit
     * @returns {Array<DictionaryWord>}
     */
    get_gram_dictionary_entries(search_query, limit) {
        const ret = wasm.deck_get_gram_dictionary_entries(this.__wbg_ptr, isLikeNone(search_query) ? 0 : addToExternrefTable0(search_query), limit);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return takeFromExternrefTable0(ret[0]);
    }
    /**
     * A bounded page in the same relevance/frequency order as dictionary search.
     * Native callers must not transfer the whole dictionary through the bridge.
     * @param {string | null | undefined} search_query
     * @param {number} offset
     * @param {number} limit
     * @returns {Array<DictionaryWord>}
     */
    get_gram_dictionary_page(search_query, offset, limit) {
        const ret = wasm.deck_get_gram_dictionary_page(this.__wbg_ptr, isLikeNone(search_query) ? 0 : addToExternrefTable0(search_query), offset, limit);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return takeFromExternrefTable0(ret[0]);
    }
    /**
     * Get all cards that have been detected as leeches (12+ lapses)
     * @returns {Array<CardSummary>}
     */
    get_leeches() {
        const ret = wasm.deck_get_leeches(this.__wbg_ptr);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return takeFromExternrefTable0(ret[0]);
    }
    /**
     * The daily lockup offer ("Let's review these 15 cards today").
     * Non-None when more than `REVIEW_LOCKUP_TRIGGER` cards are due and the
     * user hasn't locked up yet today; keeps the `REVIEW_LOCKUP_KEEP`
     * most-due cards active and sets the rest aside.
     * @param {Array<ChallengeRequirements>} banned_challenge_types
     * @param {number} timestamp_ms
     * @returns {(LockupOffer | undefined)}
     */
    get_lockup_offer(banned_challenge_types, timestamp_ms) {
        const ret = wasm.deck_get_lockup_offer(this.__wbg_ptr, banned_challenge_types, timestamp_ms);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return takeFromExternrefTable0(ret[0]);
    }
    /**
     * Compute a manual add option for a specific card type. Call lazily (e.g. on dropdown open).
     * @param {CardType} card_type
     * @param {SentenceListSelection | null} [sentence_list]
     * @returns {(ManualAddOption | undefined)}
     */
    get_manual_add_option(card_type, sentence_list) {
        const ret = wasm.deck_get_manual_add_option(this.__wbg_ptr, card_type, isLikeNone(sentence_list) ? 0 : addToExternrefTable0(sentence_list));
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return takeFromExternrefTable0(ret[0]);
    }
    /**
     * Choices for the manual-add picker. Call lazily when the picker opens.
     * @param {SentenceListSelection | null | undefined} sentence_list
     * @param {boolean} is_signed_in
     * @returns {Array<ManualAddOption>}
     */
    get_manual_add_options(sentence_list, is_signed_in) {
        const ret = wasm.deck_get_manual_add_options(this.__wbg_ptr, isLikeNone(sentence_list) ? 0 : addToExternrefTable0(sentence_list), is_signed_in);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return takeFromExternrefTable0(ret[0]);
    }
    /**
     * @param {Array<string>} movie_ids
     * @returns {Array<MovieMetadataBasic>}
     */
    get_movie_metadata(movie_ids) {
        const ret = wasm.deck_get_movie_metadata(this.__wbg_ptr, movie_ids);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return takeFromExternrefTable0(ret[0]);
    }
    /**
     * @param {string} movie_id
     * @returns {(Uint8Array | undefined)}
     */
    get_movie_poster(movie_id) {
        const ptr0 = passStringToWasm0(movie_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.deck_get_movie_poster(this.__wbg_ptr, ptr0, len0);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return takeFromExternrefTable0(ret[0]);
    }
    /**
     * Full movie-browser statistics, including the milestone card counts.
     * @returns {Array<MovieStats>}
     */
    get_movie_stats() {
        const ret = wasm.deck_get_movie_stats(this.__wbg_ptr);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return takeFromExternrefTable0(ret[0]);
    }
    /**
     * @returns {Language}
     */
    get_native_language() {
        const ret = wasm.deck_get_native_language(this.__wbg_ptr);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return takeFromExternrefTable0(ret[0]);
    }
    /**
     * Compute everything the NoCardsReady screen needs in a single call.
     * This calls next_unknown_cards only once.
     * @param {Array<ChallengeRequirements>} banned_challenge_types
     * @param {SentenceListSelection | null} [sentence_list]
     * @returns {NoCardsReadyInfo}
     */
    get_no_cards_ready_info(banned_challenge_types, sentence_list) {
        const ret = wasm.deck_get_no_cards_ready_info(this.__wbg_ptr, banned_challenge_types, isLikeNone(sentence_list) ? 0 : addToExternrefTable0(sentence_list));
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return takeFromExternrefTable0(ret[0]);
    }
    /**
     * @returns {number}
     */
    get_percent_of_words_known() {
        const ret = wasm.deck_get_percent_of_words_known(this.__wbg_ptr);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return ret[0];
    }
    /**
     * @returns {Array<PimsleurStats>}
     */
    get_pimsleur_stats() {
        const ret = wasm.deck_get_pimsleur_stats(this.__wbg_ptr);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return takeFromExternrefTable0(ret[0]);
    }
    /**
     * Get placement test words distributed by likelihood of knowledge
     * Takes lists of known and unknown words as strings, builds a regression, and returns
     * words at different knowledge probability levels (1%, 10%, 20%, ..., 99%)
     * @param {Array<string>} known_words
     * @param {Array<string>} unknown_words
     * @returns {Array<PlacementTestWord>}
     */
    get_placement_test(known_words, unknown_words) {
        const ret = wasm.deck_get_placement_test(this.__wbg_ptr, known_words, unknown_words);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return takeFromExternrefTable0(ret[0]);
    }
    /**
     * The "Review N more cards" offer: prefers allowed locked cards, then due order.
     * None when no locked card is actually due — locked cards scheduled for
     * the future are morally just future cards, so they don't keep the user
     * in "release mode" (or block adding new cards).
     * @param {Array<ChallengeRequirements>} banned
     * @param {number} timestamp_ms
     * @returns {(ReleaseOffer | undefined)}
     */
    get_release_offer(banned, timestamp_ms) {
        const ret = wasm.deck_get_release_offer(this.__wbg_ptr, banned, timestamp_ms);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return takeFromExternrefTable0(ret[0]);
    }
    /**
     * @param {Array<ChallengeRequirements>} banned_challenge_types
     * @param {number} timestamp_ms
     * @returns {ReviewInfo}
     */
    get_review_info(banned_challenge_types, timestamp_ms) {
        const ret = wasm.deck_get_review_info(this.__wbg_ptr, banned_challenge_types, timestamp_ms);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return ReviewInfo.__wrap(ret[0]);
    }
    /**
     * @returns {(SentenceListSelection | undefined)}
     */
    get_sentence_list() {
        const ret = wasm.deck_get_sentence_list(this.__wbg_ptr);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return takeFromExternrefTable0(ret[0]);
    }
    /**
     * The list a category opens on: the last one chosen in it, else the best.
     * Called only when switching categories, so choosing the best movie stays lazy.
     * @param {SentenceListCategory} category
     * @returns {(SentenceListSelection | undefined)}
     */
    get_sentence_list_for_category(category) {
        const ret = wasm.deck_get_sentence_list_for_category(this.__wbg_ptr, category);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return takeFromExternrefTable0(ret[0]);
    }
    /**
     * Essential progress refers to the tier chosen by smart-add, which may
     * differ from the first incomplete tier when the proposed cards finish it.
     * @param {SentenceListSelection | null | undefined} selection
     * @param {number} essential_percent_known
     * @returns {SentenceListProgress}
     */
    get_sentence_list_progress(selection, essential_percent_known) {
        const ret = wasm.deck_get_sentence_list_progress(this.__wbg_ptr, isLikeNone(selection) ? 0 : addToExternrefTable0(selection), essential_percent_known);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return takeFromExternrefTable0(ret[0]);
    }
    /**
     * @returns {Language}
     */
    get_target_language() {
        const ret = wasm.deck_get_target_language(this.__wbg_ptr);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return takeFromExternrefTable0(ret[0]);
    }
    /**
     * @returns {number}
     */
    get_today_reviews() {
        const ret = wasm.deck_get_today_reviews(this.__wbg_ptr);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return ret[0] >>> 0;
    }
    /**
     * @returns {TodaySummary}
     */
    get_today_summary() {
        const ret = wasm.deck_get_today_summary(this.__wbg_ptr);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return takeFromExternrefTable0(ret[0]);
    }
    /**
     * Today's estimated time spent reviewing, in seconds.
     * @returns {number}
     */
    get_today_time_spent() {
        const ret = wasm.deck_get_today_time_spent(this.__wbg_ptr);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return ret[0] >>> 0;
    }
    /**
     * @returns {bigint}
     */
    get_total_reviews() {
        const ret = wasm.deck_get_total_reviews(this.__wbg_ptr);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return BigInt.asUintN(64, ret[0]);
    }
    /**
     * @returns {number}
     */
    get_xp() {
        const ret = wasm.deck_get_xp(this.__wbg_ptr);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return ret[0];
    }
    /**
     * Build the whole word containing any member sense's frequency index.
     * Returns None for an invalid index or a word with no defined senses.
     * @param {number} frequency_index
     * @returns {(DictionaryWord | undefined)}
     */
    gram_dictionary_entry(frequency_index) {
        const ret = wasm.deck_gram_dictionary_entry(this.__wbg_ptr, frequency_index);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return takeFromExternrefTable0(ret[0]);
    }
    /**
     * @returns {boolean}
     */
    has_taken_placement_test() {
        const ret = wasm.deck_has_taken_placement_test(this.__wbg_ptr);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return ret[0] !== 0;
    }
    /**
     * @param {ReviewScreenInputs} inputs
     * @returns {HomeScreenView}
     */
    home_screen_view(inputs) {
        const ret = wasm.deck_home_screen_view(this.__wbg_ptr, inputs);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return takeFromExternrefTable0(ret[0]);
    }
    /**
     * How many cards are currently set aside in lockup.
     * @returns {number}
     */
    locked_count() {
        const ret = wasm.deck_locked_count(this.__wbg_ptr);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return ret[0] >>> 0;
    }
    /**
     * Count of cards the user has explicitly added (excludes ghosts).
     * Use this for onboarding/regime decisions and any "how much have you
     * engaged with this deck" UX — a ghost card represents a word the user
     * happened to encounter in a sentence, not a deliberate add.
     * @returns {number}
     */
    num_cards_added() {
        const ret = wasm.deck_num_cards_added(this.__wbg_ptr);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return ret[0] >>> 0;
    }
    /**
     * Added cards the user has reviewed at least once, including ones they already
     * knew (which drop out of scheduling, so the due/future lists don't count them).
     * @returns {number}
     */
    num_cards_studied() {
        const ret = wasm.deck_num_cards_studied(this.__wbg_ptr);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return ret[0] >>> 0;
    }
    /**
     * None means that a rebuilt deck produced the same round: preserve the
     * host's state object and selected words during background pack loading.
     * @param {PlacementSession} session
     * @returns {(PlacementSession | undefined)}
     */
    refresh_placement_session(session) {
        const ret = wasm.deck_refresh_placement_session(this.__wbg_ptr, session);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return takeFromExternrefTable0(ret[0]);
    }
    /**
     * @param {Array<CardReview>} reviews
     * @returns {(DeckEvent | undefined)}
     */
    review_cards(reviews) {
        const ret = wasm.deck_review_cards(this.__wbg_ptr, reviews);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return takeFromExternrefTable0(ret[0]);
    }
    /**
     * Selects PlacementTest → ReviewPlan → SetDisplayName → Accomplishment
     * → held (or next) Challenge → Idle, in that priority order on both hosts.
     * A held challenge takes precedence over idle even when nothing is due.
     * Hosts retain a selected challenge until the deck or restrictions change;
     * `None` is never held, so newly ready challenges can surface from idle.
     * @param {ReviewScreenInputs} inputs
     * @returns {ReviewScreenView}
     */
    review_screen_view(inputs) {
        const ret = wasm.deck_review_screen_view(this.__wbg_ptr, inputs);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return takeFromExternrefTable0(ret[0]);
    }
    /**
     * Keep sentence attribution without repeating the film already shown in the clip header.
     * @param {Array<string>} movie_ids
     * @param {string | null} [shown_in_clip]
     * @returns {Array<MovieMetadataBasic>}
     */
    sentence_posters(movie_ids, shown_in_clip) {
        const ret = wasm.deck_sentence_posters(this.__wbg_ptr, movie_ids, isLikeNone(shown_in_clip) ? 0 : addToExternrefTable0(shown_in_clip));
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return takeFromExternrefTable0(ret[0]);
    }
    /**
     * Zero extra sentences rebuilds the latest deck without running a simulation.
     * @param {string} base_deck_id
     * @param {number} extra_sentences
     * @param {number} timestamp_ms
     * @returns {AnkiDeckPlanner}
     */
    start_anki_deck_extension(base_deck_id, extra_sentences, timestamp_ms) {
        const ptr0 = passStringToWasm0(base_deck_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.deck_start_anki_deck_extension(this.__wbg_ptr, ptr0, len0, extra_sentences, timestamp_ms);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return AnkiDeckPlanner.__wrap(ret[0]);
    }
    /**
     * @param {AnkiDeckOptions} options
     * @param {number} timestamp_ms
     * @returns {AnkiDeckPlanner}
     */
    start_anki_deck_plan(options, timestamp_ms) {
        const ret = wasm.deck_start_anki_deck_plan(this.__wbg_ptr, options, timestamp_ms);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return AnkiDeckPlanner.__wrap(ret[0]);
    }
    /**
     * @returns {PlacementSession}
     */
    start_placement_session() {
        const ret = wasm.deck_start_placement_session(this.__wbg_ptr);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return takeFromExternrefTable0(ret[0]);
    }
    /**
     * Begin a chunked simulation of future usage starting at `timestamp_ms`,
     * adding `new_cards_per_day` cards each simulated day (None uses the
     * app's default smart-add batch size).
     * The deck itself is not modified; the simulation runs on a clone.
     * @param {number} timestamp_ms
     * @param {number | null} [new_cards_per_day]
     * @returns {DeckSimulation}
     */
    start_simulation(timestamp_ms, new_cards_per_day) {
        const ret = wasm.deck_start_simulation(this.__wbg_ptr, timestamp_ms, isLikeNone(new_cards_per_day) ? 0 : addToExternrefTable0(new_cards_per_day));
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return DeckSimulation.__wrap(ret[0]);
    }
    /**
     * @param {Array<ChallengeRequirements>} banned
     * @param {number} timestamp_ms
     * @returns {StatsScreenView}
     */
    stats_screen_view(banned, timestamp_ms) {
        const ret = wasm.deck_stats_screen_view(this.__wbg_ptr, banned, timestamp_ms);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return takeFromExternrefTable0(ret[0]);
    }
    /**
     * Whether the current study plan was accepted within the last 36 hours.
     * @param {number} timestamp_ms
     * @returns {boolean}
     */
    study_plan_was_recently_accepted(timestamp_ms) {
        const ret = wasm.deck_study_plan_was_recently_accepted(this.__wbg_ptr, timestamp_ms);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return ret[0] !== 0;
    }
    /**
     * @param {string} access_token
     * @returns {Promise<void>}
     */
    submit_language_stats(access_token) {
        const ptr0 = passStringToWasm0(access_token, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.deck_submit_language_stats(this.__wbg_ptr, ptr0, len0);
        return ret;
    }
    /**
     * @param {string} access_token
     * @param {string} user_id
     * @returns {Promise<void>}
     */
    submit_push_notifications(access_token, user_id) {
        const ptr0 = passStringToWasm0(access_token, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passStringToWasm0(user_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len1 = WASM_VECTOR_LEN;
        const ret = wasm.deck_submit_push_notifications(this.__wbg_ptr, ptr0, len0, ptr1, len1);
        return ret;
    }
    /**
     * @param {Array<PartGraded>} challenge
     * @returns {(DeckEvent | undefined)}
     */
    transcribe_sentence(challenge) {
        const ret = wasm.deck_transcribe_sentence(this.__wbg_ptr, challenge);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return takeFromExternrefTable0(ret[0]);
    }
    /**
     * @param {Uint32Array} hinted_literal_indices
     * @param {string} challenge_sentence
     * @returns {(DeckEvent | undefined)}
     */
    translate_sentence_perfect(hinted_literal_indices, challenge_sentence) {
        const ptr0 = passStringToWasm0(challenge_sentence, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.deck_translate_sentence_perfect(this.__wbg_ptr, hinted_literal_indices, ptr0, len0);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return takeFromExternrefTable0(ret[0]);
    }
    /**
     * Create a Graded translation challenge event.
     *
     * `literal_grades` should have one entry per literal in the sentence (same order as
     * `target_language_literals` from the challenge). None for Other word types or unknown,
     * Some(Remembered/Forgot) for heteronyms.
     * @param {string} challenge_sentence
     * @param {string} submission
     * @param {LiteralGrades} literal_grades
     * @param {Uint32Array} hinted_literal_indices
     * @param {Array<TaggedGram<Gram<string>>>} phrases_remembered
     * @param {Array<TaggedGram<Gram<string>>>} phrases_forgot
     * @returns {(DeckEvent | undefined)}
     */
    translate_sentence_wrong(challenge_sentence, submission, literal_grades, hinted_literal_indices, phrases_remembered, phrases_forgot) {
        const ptr0 = passStringToWasm0(challenge_sentence, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passStringToWasm0(submission, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len1 = WASM_VECTOR_LEN;
        const ret = wasm.deck_translate_sentence_wrong(this.__wbg_ptr, ptr0, len0, ptr1, len1, literal_grades, hinted_literal_indices, phrases_remembered, phrases_forgot);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return takeFromExternrefTable0(ret[0]);
    }
    /**
     * A page of added cards, soonest due first.
     * @param {number} page
     * @returns {YourCardsScreenView}
     */
    your_cards_view(page) {
        const ret = wasm.deck_your_cards_view(this.__wbg_ptr, page);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return takeFromExternrefTable0(ret[0]);
    }
}
if (Symbol.dispose) Deck.prototype[Symbol.dispose] = Deck.prototype.free;

/**
 * A paused simulation of future deck usage. The frontend advances it in
 * chunks (e.g. two weeks at a time), yielding to the main thread between
 * chunks and rendering the samples incrementally.
 */
export class DeckSimulation {
    static __wrap(ptr) {
        const obj = Object.create(DeckSimulation.prototype);
        obj.__wbg_ptr = ptr;
        DeckSimulationFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        DeckSimulationFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_decksimulation_free(ptr, 0);
    }
    /**
     * Advance the simulation by `days` days, returning one sample per day.
     * @param {number} days
     * @returns {Array<SimulationSample>}
     */
    simulate_days(days) {
        const ret = wasm.decksimulation_simulate_days(this.__wbg_ptr, days);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return takeFromExternrefTable0(ret[0]);
    }
}
if (Symbol.dispose) DeckSimulation.prototype[Symbol.dispose] = DeckSimulation.prototype.free;

export class DictionaryWord {
    static __wrap(ptr) {
        const obj = Object.create(DictionaryWord.prototype);
        obj.__wbg_ptr = ptr;
        DictionaryWordFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        DictionaryWordFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_dictionaryword_free(ptr, 0);
    }
    /**
     * @returns {AudioRequest}
     */
    get audio_request() {
        const ret = wasm.dictionaryword_audio_request(this.__wbg_ptr);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return takeFromExternrefTable0(ret[0]);
    }
    /**
     * @returns {string}
     */
    get display_text() {
        let deferred2_0;
        let deferred2_1;
        try {
            const ret = wasm.dictionaryword_display_text(this.__wbg_ptr);
            var ptr1 = ret[0];
            var len1 = ret[1];
            if (ret[3]) {
                ptr1 = 0; len1 = 0;
                throw takeFromExternrefTable0(ret[2]);
            }
            deferred2_0 = ptr1;
            deferred2_1 = len1;
            return getStringFromWasm0(ptr1, len1);
        } finally {
            wasm.__wbindgen_free(deferred2_0, deferred2_1, 1);
        }
    }
    /**
     * @returns {number}
     */
    get frequency_index() {
        const ret = wasm.dictionaryword_frequency_index(this.__wbg_ptr);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return ret[0] >>> 0;
    }
    /**
     * A short word-level summary, taking the first meaning of up to two senses.
     * @returns {string}
     */
    get gloss() {
        let deferred2_0;
        let deferred2_1;
        try {
            const ret = wasm.dictionaryword_gloss(this.__wbg_ptr);
            var ptr1 = ret[0];
            var len1 = ret[1];
            if (ret[3]) {
                ptr1 = 0; len1 = 0;
                throw takeFromExternrefTable0(ret[2]);
            }
            deferred2_0 = ptr1;
            deferred2_1 = len1;
            return getStringFromWasm0(ptr1, len1);
        } finally {
            wasm.__wbindgen_free(deferred2_0, deferred2_1, 1);
        }
    }
    /**
     * @returns {boolean}
     */
    get is_phrase() {
        const ret = wasm.dictionaryword_is_phrase(this.__wbg_ptr);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return ret[0] !== 0;
    }
    /**
     * @returns {(WordPrefix | undefined)}
     */
    get prefix() {
        const ret = wasm.dictionaryword_prefix(this.__wbg_ptr);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return takeFromExternrefTable0(ret[0]);
    }
    /**
     * @returns {Array<RubySegment>}
     */
    get ruby() {
        const ret = wasm.dictionaryword_ruby(this.__wbg_ptr);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return takeFromExternrefTable0(ret[0]);
    }
    /**
     * @returns {Array<DictionarySense>}
     */
    get senses() {
        const ret = wasm.dictionaryword_senses(this.__wbg_ptr);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return takeFromExternrefTable0(ret[0]);
    }
    /**
     * @returns {Language}
     */
    get target_language() {
        const ret = wasm.dictionaryword_target_language(this.__wbg_ptr);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return takeFromExternrefTable0(ret[0]);
    }
}
if (Symbol.dispose) DictionaryWord.prototype[Symbol.dispose] = DictionaryWord.prototype.free;

export class ListenerKey {
    static __wrap(ptr) {
        const obj = Object.create(ListenerKey.prototype);
        obj.__wbg_ptr = ptr;
        ListenerKeyFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    static __unwrap(jsValue) {
        if (!(jsValue instanceof ListenerKey)) {
            return 0;
        }
        return jsValue.__destroy_into_raw();
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        ListenerKeyFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_listenerkey_free(ptr, 0);
    }
}
if (Symbol.dispose) ListenerKey.prototype[Symbol.dispose] = ListenerKey.prototype.free;

/**
 * The daily offer to set aside ("lock up") all but the most-due cards.
 */
export class LockupOffer {
    static __wrap(ptr) {
        const obj = Object.create(LockupOffer.prototype);
        obj.__wbg_ptr = ptr;
        LockupOfferFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        LockupOfferFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_lockupoffer_free(ptr, 0);
    }
    /**
     * The cards that stay active — exactly what the user is shown and approves.
     * @returns {Array<CardSummary>}
     */
    get keep_preview() {
        const ret = wasm.lockupoffer_keep_preview(this.__wbg_ptr);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return takeFromExternrefTable0(ret[0]);
    }
    /**
     * @returns {DeckEvent}
     */
    get lock_event() {
        const ret = wasm.lockupoffer_lock_event(this.__wbg_ptr);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return takeFromExternrefTable0(ret[0]);
    }
}
if (Symbol.dispose) LockupOffer.prototype[Symbol.dispose] = LockupOffer.prototype.free;

/**
 * The "Review N more cards" offer that releases cards from lockup.
 */
export class ReleaseOffer {
    static __wrap(ptr) {
        const obj = Object.create(ReleaseOffer.prototype);
        obj.__wbg_ptr = ptr;
        ReleaseOfferFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        ReleaseOfferFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_releaseoffer_free(ptr, 0);
    }
    /**
     * @returns {number}
     */
    get release_count() {
        const ret = wasm.releaseoffer_release_count(this.__wbg_ptr);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return ret[0] >>> 0;
    }
    /**
     * The cards that would be released — exactly what the user is shown.
     * @returns {Array<CardSummary>}
     */
    get release_preview() {
        const ret = wasm.releaseoffer_release_preview(this.__wbg_ptr);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return takeFromExternrefTable0(ret[0]);
    }
    /**
     * @returns {DeckEvent}
     */
    get unlock_event() {
        const ret = wasm.releaseoffer_unlock_event(this.__wbg_ptr);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return takeFromExternrefTable0(ret[0]);
    }
}
if (Symbol.dispose) ReleaseOffer.prototype[Symbol.dispose] = ReleaseOffer.prototype.free;

export class ReviewInfo {
    static __wrap(ptr) {
        const obj = Object.create(ReviewInfo.prototype);
        obj.__wbg_ptr = ptr;
        ReviewInfoFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        ReviewInfoFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_reviewinfo_free(ptr, 0);
    }
    /**
     * @returns {number}
     */
    get due_but_audio_pending_count() {
        const ret = wasm.reviewinfo_due_but_audio_pending_count(this.__wbg_ptr);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return ret[0] >>> 0;
    }
    /**
     * @returns {number}
     */
    get due_but_banned_count() {
        const ret = wasm.reviewinfo_due_but_banned_count(this.__wbg_ptr);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return ret[0] >>> 0;
    }
    /**
     * @returns {number}
     */
    get due_but_locked_count() {
        const ret = wasm.reviewinfo_due_but_locked_count(this.__wbg_ptr);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return ret[0] >>> 0;
    }
    /**
     * @returns {number}
     */
    get due_count() {
        const ret = wasm.reviewinfo_due_count(this.__wbg_ptr);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return ret[0] >>> 0;
    }
    /**
     * @returns {number}
     */
    get future_count() {
        const ret = wasm.reviewinfo_future_count(this.__wbg_ptr);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return ret[0] >>> 0;
    }
    /**
     * @returns {number}
     */
    get total_count() {
        const ret = wasm.reviewinfo_total_count(this.__wbg_ptr);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return ret[0] >>> 0;
    }
}
if (Symbol.dispose) ReviewInfo.prototype[Symbol.dispose] = ReviewInfo.prototype.free;

export class Weapon {
    static __wrap(ptr) {
        const obj = Object.create(Weapon.prototype);
        obj.__wbg_ptr = ptr;
        WeaponFinalization.register(obj, obj.__wbg_ptr, obj);
        return obj;
    }
    __destroy_into_raw() {
        const ptr = this.__wbg_ptr;
        this.__wbg_ptr = 0;
        WeaponFinalization.unregister(this);
        return ptr;
    }
    free() {
        const ptr = this.__destroy_into_raw();
        wasm.__wbg_weapon_free(ptr, 0);
    }
    /**
     * @param {DeckEvent} event
     */
    add_deck_event(event) {
        const ret = wasm.weapon_add_deck_event(this.__wbg_ptr, event);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return takeFromExternrefTable0(ret[0]);
    }
    /**
     * Add a deck event with a specific timestamp (milliseconds since Unix epoch).
     * Used when an exercise was completed earlier but the event is being submitted later.
     * @param {DeckEvent} event
     * @param {number} timestamp_ms
     */
    add_deck_event_at(event, timestamp_ms) {
        const ret = wasm.weapon_add_deck_event_at(this.__wbg_ptr, event, timestamp_ms);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return takeFromExternrefTable0(ret[0]);
    }
    /**
     * @param {DeckSelectionEvent} event
     */
    add_deck_selection_event(event) {
        const ret = wasm.weapon_add_deck_selection_event(this.__wbg_ptr, event);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return takeFromExternrefTable0(ret[0]);
    }
    /**
     * @param {string} device_id
     * @param {string} stream_id
     * @param {string} event
     */
    add_remote_event(device_id, stream_id, event) {
        const ptr0 = passStringToWasm0(device_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passStringToWasm0(stream_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len1 = WASM_VECTOR_LEN;
        const ptr2 = passStringToWasm0(event, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len2 = WASM_VECTOR_LEN;
        const ret = wasm.weapon_add_remote_event(this.__wbg_ptr, ptr0, len0, ptr1, len1, ptr2, len2);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return takeFromExternrefTable0(ret[0]);
    }
    /**
     * @param {Course} course
     * @returns {Promise<void>}
     */
    cache_language_pack(course) {
        const ret = wasm.weapon_cache_language_pack(this.__wbg_ptr, course);
        return ret;
    }
    /**
     * @param {string | null | undefined} user_id
     * @param {Function} sync_stream
     * @returns {Promise<Weapon>}
     */
    static create(user_id, sync_stream) {
        const ret = wasm.weapon_create(isLikeNone(user_id) ? 0 : addToExternrefTable0(user_id), sync_stream);
        return ret;
    }
    /**
     * Identifies everything `get_deck_state` reads for this course: the review
     * and deck-selection logs, which half of the pack is loaded, and whether the
     * review history is known. Hosts rebuild their Deck snapshot when this
     * differs from the one they built against, and never otherwise, so a no-op
     * sync can't replace the deck (and the challenge) a learner is in the
     * middle of.
     * @param {Course} course
     * @returns {string}
     */
    deck_inputs_key(course) {
        let deferred2_0;
        let deferred2_1;
        try {
            const ret = wasm.weapon_deck_inputs_key(this.__wbg_ptr, course);
            var ptr1 = ret[0];
            var len1 = ret[1];
            if (ret[3]) {
                ptr1 = 0; len1 = 0;
                throw takeFromExternrefTable0(ret[2]);
            }
            deferred2_0 = ptr1;
            deferred2_1 = len1;
            return getStringFromWasm0(ptr1, len1);
        } finally {
            wasm.__wbindgen_free(deferred2_0, deferred2_1, 1);
        }
    }
    /**
     * @param {string} access_token
     * @returns {Promise<void>}
     */
    delete_account(access_token) {
        const ptr0 = passStringToWasm0(access_token, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.weapon_delete_account(this.__wbg_ptr, ptr0, len0);
        return ret;
    }
    /**
     * @returns {string}
     */
    get device_id() {
        let deferred2_0;
        let deferred2_1;
        try {
            const ret = wasm.weapon_device_id(this.__wbg_ptr);
            var ptr1 = ret[0];
            var len1 = ret[1];
            if (ret[3]) {
                ptr1 = 0; len1 = 0;
                throw takeFromExternrefTable0(ret[2]);
            }
            deferred2_0 = ptr1;
            deferred2_1 = len1;
            return getStringFromWasm0(ptr1, len1);
        } finally {
            wasm.__wbindgen_free(deferred2_0, deferred2_1, 1);
        }
    }
    /**
     * @returns {(DeckSelection | undefined)}
     */
    get_deck_selection_state() {
        const ret = wasm.weapon_get_deck_selection_state(this.__wbg_ptr);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return takeFromExternrefTable0(ret[0]);
    }
    /**
     * Fold the review log into a deck for this course.
     *
     * Returns `None` while only the core half of the pack is loaded and the
     * learner would not be offered the placement test: sentence reviews
     * can't be folded without the sentence half, so that deck would be a
     * stale one. Hosts keep showing their loading state and call again once
     * `load_language_pack` completes.
     * @param {Course} course
     * @param {number} utc_offset_seconds
     * @returns {Promise<(Deck | undefined)>}
     */
    get_deck_state(course, utc_offset_seconds) {
        const ret = wasm.weapon_get_deck_state(this.__wbg_ptr, course, utc_offset_seconds);
        return ret;
    }
    /**
     * @param {string} stream_id
     * @returns {(number | undefined)}
     */
    get_stream_num_events(stream_id) {
        const ptr0 = passStringToWasm0(stream_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.weapon_get_stream_num_events(this.__wbg_ptr, ptr0, len0);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return takeFromExternrefTable0(ret[0]);
    }
    /**
     * @param {SyncTarget} target
     * @returns {SyncState<string, string>}
     */
    get_sync_state(target) {
        const ret = wasm.weapon_get_sync_state(this.__wbg_ptr, target);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return takeFromExternrefTable0(ret[0]);
    }
    /**
     * @param {SyncTarget} target
     * @returns {(EarliestUnsyncedEvent | undefined)}
     */
    get_timestamp_of_earliest_unsynced_event(target) {
        const ret = wasm.weapon_get_timestamp_of_earliest_unsynced_event(this.__wbg_ptr, target);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return takeFromExternrefTable0(ret[0]);
    }
    /**
     * Whether the loaded pack for this course (if any) includes the
     * sentence half.
     * @param {Course} course
     * @returns {boolean}
     */
    is_language_pack_fully_loaded(course) {
        const ret = wasm.weapon_is_language_pack_fully_loaded(this.__wbg_ptr, course);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return ret[0] !== 0;
    }
    /**
     * @param {Language} language
     * @param {string} slug
     * @param {BookPosition | null} [position]
     * @returns {Promise<BookReaderView>}
     */
    load_book(language, slug, position) {
        const ptr0 = passStringToWasm0(slug, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.weapon_load_book(this.__wbg_ptr, language, ptr0, len0, isLikeNone(position) ? 0 : addToExternrefTable0(position));
        return ret;
    }
    /**
     * @param {string} stream_id
     * @returns {Promise<void>}
     */
    load_from_local_storage(stream_id) {
        const ptr0 = passStringToWasm0(stream_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.weapon_load_from_local_storage(this.__wbg_ptr, ptr0, len0);
        return ret;
    }
    /**
     * Load the full language pack (core + sentences), replacing a core-only
     * pack if one was loaded first.
     * @param {Course} course
     * @param {Function | null} [on_progress]
     * @returns {Promise<void>}
     */
    load_language_pack(course, on_progress) {
        const ret = wasm.weapon_load_language_pack(this.__wbg_ptr, course, isLikeNone(on_progress) ? 0 : addToExternrefTable0(on_progress));
        return ret;
    }
    /**
     * Load just the core half (dictionary + frequencies) — enough to create
     * a deck and run the placement test while the sentence half is still
     * downloading. A later `load_language_pack` call swaps in the full pack.
     * @param {Course} course
     * @param {Function | null} [on_progress]
     * @returns {Promise<void>}
     */
    load_language_pack_core(course, on_progress) {
        const ret = wasm.weapon_load_language_pack_core(this.__wbg_ptr, course, isLikeNone(on_progress) ? 0 : addToExternrefTable0(on_progress));
        return ret;
    }
    /**
     * @returns {number}
     */
    get num_events() {
        const ret = wasm.weapon_num_events(this.__wbg_ptr);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return ret[0] >>> 0;
    }
    /**
     * @param {SyncTarget} target
     * @returns {number}
     */
    num_events_on_remote_as_of_last_sync(target) {
        const ret = wasm.weapon_num_events_on_remote_as_of_last_sync(this.__wbg_ptr, target);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return ret[0] >>> 0;
    }
    request_streams() {
        const ret = wasm.weapon_request_streams(this.__wbg_ptr);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return takeFromExternrefTable0(ret[0]);
    }
    /**
     * Whether the user's review history can be treated as complete: either
     * they're anonymous (there is no server copy — local is the whole
     * truth) or the reviews stream has finished at least one download from
     * Supabase this session. Until this is true, "no placement test event
     * locally" means *unknown*, not "never took it" — the placement test
     * must not be offered off an unconfirmed history.
     * @returns {boolean}
     */
    reviews_history_known() {
        const ret = wasm.weapon_reviews_history_known(this.__wbg_ptr);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return ret[0] !== 0;
    }
    /**
     * @param {Language} language
     * @param {string} slug
     * @param {BookPosition} position
     */
    save_book_position(language, slug, position) {
        const ptr0 = passStringToWasm0(slug, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.weapon_save_book_position(this.__wbg_ptr, language, ptr0, len0, position);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return takeFromExternrefTable0(ret[0]);
    }
    /**
     * @param {string} stream_id
     * @param {Function} callback
     * @returns {ListenerKey}
     */
    subscribe_to_stream(stream_id, callback) {
        const ptr0 = passStringToWasm0(stream_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.weapon_subscribe_to_stream(this.__wbg_ptr, ptr0, len0, callback);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return ListenerKey.__wrap(ret[0]);
    }
    /**
     * @param {string} stream_id
     * @param {string | null | undefined} access_token
     * @param {boolean} attempt_supabase
     * @param {ListenerKey | null | undefined} modifier
     * @param {boolean} upload
     * @returns {Promise<void>}
     */
    sync(stream_id, access_token, attempt_supabase, modifier, upload) {
        const ptr0 = passStringToWasm0(stream_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.weapon_sync(this.__wbg_ptr, ptr0, len0, isLikeNone(access_token) ? 0 : addToExternrefTable0(access_token), attempt_supabase, isLikeNone(modifier) ? 0 : addToExternrefTable0(modifier), upload);
        return ret;
    }
    /**
     * @param {boolean} online
     * @param {number} now_ms
     * @param {boolean} manual_sync_in_flight
     * @param {string | null} [host_sync_error]
     * @returns {SyncStatusView}
     */
    sync_status(online, now_ms, manual_sync_in_flight, host_sync_error) {
        const ret = wasm.weapon_sync_status(this.__wbg_ptr, online, now_ms, manual_sync_in_flight, isLikeNone(host_sync_error) ? 0 : addToExternrefTable0(host_sync_error));
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return takeFromExternrefTable0(ret[0]);
    }
    /**
     * @param {string} access_token
     * @param {ListenerKey | null | undefined} modifier
     * @param {boolean} upload
     * @returns {Promise<void>}
     */
    sync_with_supabase(access_token, modifier, upload) {
        const ptr0 = passStringToWasm0(access_token, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ret = wasm.weapon_sync_with_supabase(this.__wbg_ptr, ptr0, len0, isLikeNone(modifier) ? 0 : addToExternrefTable0(modifier), upload);
        return ret;
    }
    /**
     * @param {ListenerKey} key
     */
    unsubscribe(key) {
        _assertClass(key, ListenerKey);
        var ptr0 = key.__destroy_into_raw();
        const ret = wasm.weapon_unsubscribe(this.__wbg_ptr, ptr0);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return takeFromExternrefTable0(ret[0]);
    }
    /**
     * @returns {(string | undefined)}
     */
    get user_id() {
        const ret = wasm.weapon_user_id(this.__wbg_ptr);
        if (ret[2]) {
            throw takeFromExternrefTable0(ret[1]);
        }
        return takeFromExternrefTable0(ret[0]);
    }
}
if (Symbol.dispose) Weapon.prototype[Symbol.dispose] = Weapon.prototype.free;

/**
 * @returns {AccountCopy}
 */
export function account_copy() {
    const ret = wasm.account_copy();
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return takeFromExternrefTable0(ret[0]);
}

/**
 * @param {number} exported_ms
 * @param {number} now_ms
 * @returns {string}
 */
export function anki_export_relative_time(exported_ms, now_ms) {
    let deferred2_0;
    let deferred2_1;
    try {
        const ret = wasm.anki_export_relative_time(exported_ms, now_ms);
        var ptr1 = ret[0];
        var len1 = ret[1];
        if (ret[3]) {
            ptr1 = 0; len1 = 0;
            throw takeFromExternrefTable0(ret[2]);
        }
        deferred2_0 = ptr1;
        deferred2_1 = len1;
        return getStringFromWasm0(ptr1, len1);
    } finally {
        wasm.__wbindgen_free(deferred2_0, deferred2_1, 1);
    }
}

/**
 * @param {boolean} reading
 * @param {boolean} listening
 * @param {boolean} word_cards
 * @returns {AnkiOptionsCopy}
 */
export function anki_options_copy(reading, listening, word_cards) {
    const ret = wasm.anki_options_copy(reading, listening, word_cards);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return takeFromExternrefTable0(ret[0]);
}

/**
 * Operates on an owned snapshot: changing a grade must not mutate earlier
 * React state objects or a saved draft through a shared nested reference.
 * @param {Array<PartGraded>} results
 * @param {number} part_index
 * @param {number} word_index
 * @param {WordGrade} grade
 * @returns {Array<PartGraded>}
 */
export function apply_transcription_grade(results, part_index, word_index, grade) {
    const ret = wasm.apply_transcription_grade(results, part_index, word_index, grade);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return takeFromExternrefTable0(ret[0]);
}

/**
 * @param {ManualTranslationGrade} grade
 * @param {TranslationGradeItem} item
 * @param {boolean} remembered
 * @param {number} literal_count
 * @returns {ManualTranslationGrade}
 */
export function apply_translation_grade(grade, item, remembered, literal_count) {
    const ret = wasm.apply_translation_grade(grade, item, remembered, literal_count);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return takeFromExternrefTable0(ret[0]);
}

/**
 * @param {Array<PartSubmitted>} submission
 * @param {string | null | undefined} access_token
 * @param {Course} course
 * @param {MovieTitles} movie_titles
 * @returns {Promise<Grade>}
 */
export function autograde_transcription(submission, access_token, course, movie_titles) {
    const ret = wasm.autograde_transcription(submission, isLikeNone(access_token) ? 0 : addToExternrefTable0(access_token), course, movie_titles);
    return ret;
}

/**
 * @param {Array<PartSubmitted>} submission
 * @param {string | null | undefined} access_token
 * @param {Course} course
 * @param {MovieTitles} movie_titles
 * @returns {Promise<Grade>}
 */
export function autograde_transcription_llm(submission, access_token, course, movie_titles) {
    const ret = wasm.autograde_transcription_llm(submission, isLikeNone(access_token) ? 0 : addToExternrefTable0(access_token), course, movie_titles);
    return ret;
}

/**
 * @param {string} challenge_sentence
 * @param {string} user_sentence
 * @param {Array<string>} native_translations
 * @param {Array<Literal<string>>} literals
 * @param {Array<TaggedGram<Gram<string>>>} phrases
 * @param {string | null | undefined} access_token
 * @param {Course} course
 * @param {GramDefinitions} gram_definitions
 * @param {Uint32Array} literal_gram_indices
 * @param {GramDefinitions} phrase_definitions
 * @param {TaggedGram<Gram<string>>} primary_expression
 * @param {Uint32Array} primary_literal_indices
 * @param {MovieTitles} movie_titles
 * @returns {Promise<AutoGradeTranslationResponse>}
 */
export function autograde_translation(challenge_sentence, user_sentence, native_translations, literals, phrases, access_token, course, gram_definitions, literal_gram_indices, phrase_definitions, primary_expression, primary_literal_indices, movie_titles) {
    const ptr0 = passStringToWasm0(challenge_sentence, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passStringToWasm0(user_sentence, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len1 = WASM_VECTOR_LEN;
    const ret = wasm.autograde_translation(ptr0, len0, ptr1, len1, native_translations, literals, phrases, isLikeNone(access_token) ? 0 : addToExternrefTable0(access_token), course, gram_definitions, literal_gram_indices, phrase_definitions, primary_expression, primary_literal_indices, movie_titles);
    return ret;
}

/**
 * @param {BackgroundTheme} theme
 * @returns {BackgroundPalette}
 */
export function background_palette(theme) {
    const ret = wasm.background_palette(theme);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return takeFromExternrefTable0(ret[0]);
}

/**
 * Starts the sentence tutor on a tap at a UTF-16 offset into one of the reader's paragraphs.
 * @param {BookReaderView} view
 * @param {Language} language
 * @param {Language} native_language
 * @param {number} paragraph_index
 * @param {number} utf16_offset
 * @returns {BookSentenceStep}
 */
export function book_sentence_start(view, language, native_language, paragraph_index, utf16_offset) {
    const ret = wasm.book_sentence_start(view, language, native_language, paragraph_index, utf16_offset);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return takeFromExternrefTable0(ret[0]);
}

/**
 * @param {BookSentenceState} state
 * @param {BookSentenceEvent} event
 * @returns {BookSentenceStep}
 */
export function book_sentence_transition(state, event) {
    const ret = wasm.book_sentence_transition(state, event);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return takeFromExternrefTable0(ret[0]);
}

/**
 * @param {BookSentenceState} state
 * @returns {BookSentenceView}
 */
export function book_sentence_view(state) {
    const ret = wasm.book_sentence_view(state);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return takeFromExternrefTable0(ret[0]);
}

/**
 * @returns {BooksCopy}
 */
export function books_copy() {
    const ret = wasm.books_copy();
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return takeFromExternrefTable0(ret[0]);
}

/**
 * @param {SentenceChatRequest} request
 * @param {string | null} [access_token]
 * @returns {Promise<SentenceChatReply>}
 */
export function chat_book_sentence(request, access_token) {
    const ret = wasm.chat_book_sentence(request, isLikeNone(access_token) ? 0 : addToExternrefTable0(access_token));
    return ret;
}

/**
 * @returns {DeckLoadState}
 */
export function deck_load_start() {
    const ret = wasm.deck_load_start();
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return takeFromExternrefTable0(ret[0]);
}

/**
 * @param {DeckLoadState} state
 * @param {DeckLoadEvent} event
 * @returns {DeckLoadStep}
 */
export function deck_load_transition(state, event) {
    const ret = wasm.deck_load_transition(state, event);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return takeFromExternrefTable0(ret[0]);
}

/**
 * @param {DeckLoadState} state
 * @returns {DeckLoadView}
 */
export function deck_load_view(state) {
    const ret = wasm.deck_load_view(state);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return takeFromExternrefTable0(ret[0]);
}

/**
 * @param {GramDefinition} definition
 * @returns {DefinitionView}
 */
export function definition_view(definition) {
    const ret = wasm.definition_view(definition);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return takeFromExternrefTable0(ret[0]);
}

/**
 * @returns {Palette}
 */
export function design_palette() {
    const ret = wasm.design_palette();
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return takeFromExternrefTable0(ret[0]);
}

/**
 * @returns {Array<string>}
 */
export function event_streams() {
    const ret = wasm.event_streams();
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return takeFromExternrefTable0(ret[0]);
}

/**
 * @param {ExplainSentenceRequest} request
 * @param {string | null} [access_token]
 * @returns {Promise<SentenceExplanation>}
 */
export function explain_book_sentence(request, access_token) {
    const ret = wasm.explain_book_sentence(request, isLikeNone(access_token) ? 0 : addToExternrefTable0(access_token));
    return ret;
}

/**
 * Offline/manual review uses the same fallback as a failed network grade.
 * @param {Array<PartSubmitted>} submission
 * @param {Course} course
 * @returns {Grade}
 */
export function failed_transcription_review(submission, course) {
    const ret = wasm.failed_transcription_review(submission, course);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return takeFromExternrefTable0(ret[0]);
}

/**
 * @param {number} literal_count
 * @param {string} error
 * @returns {ManualTranslationGrade}
 */
export function failed_translation_review(literal_count, error) {
    const ptr0 = passStringToWasm0(error, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.failed_translation_review(literal_count, ptr0, len0);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return takeFromExternrefTable0(ret[0]);
}

/**
 * @param {string} user_translation
 * @param {Array<string>} candidates
 * @param {Language} language
 * @returns {(string | undefined)}
 */
export function find_closest_translation(user_translation, candidates, language) {
    const ptr0 = passStringToWasm0(user_translation, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.find_closest_translation(ptr0, len0, candidates, language);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return takeFromExternrefTable0(ret[0]);
}

/**
 * @param {FlashCard} flashcard
 * @param {boolean} is_new
 * @param {number} total_card_count
 * @param {number} times_type_seen
 * @param {Language} target_language
 * @param {Language} native_language
 * @returns {FlashcardView}
 */
export function flashcard_view(flashcard, is_new, total_card_count, times_type_seen, target_language, native_language) {
    const ret = wasm.flashcard_view(flashcard, is_new, total_card_count, times_type_seen, target_language, native_language);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return takeFromExternrefTable0(ret[0]);
}

/**
 * @param {string} user_id
 * @param {string} access_token
 * @returns {Promise<FollowResponse>}
 */
export function follow_user(user_id, access_token) {
    const ptr0 = passStringToWasm0(user_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passStringToWasm0(access_token, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len1 = WASM_VECTOR_LEN;
    const ret = wasm.follow_user(ptr0, len0, ptr1, len1);
    return ret;
}

/**
 * Both hosts use the same rank ticks and compact labels.
 * @returns {Array<FrequencyKnowledgeTick>}
 */
export function frequency_knowledge_ticks() {
    const ret = wasm.frequency_knowledge_ticks();
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return takeFromExternrefTable0(ret[0]);
}

/**
 * @param {number} rank
 * @returns {string}
 */
export function frequency_rank_label(rank) {
    let deferred2_0;
    let deferred2_1;
    try {
        const ret = wasm.frequency_rank_label(rank);
        var ptr1 = ret[0];
        var len1 = ret[1];
        if (ret[3]) {
            ptr1 = 0; len1 = 0;
            throw takeFromExternrefTable0(ret[2]);
        }
        deferred2_0 = ptr1;
        deferred2_1 = len1;
        return getStringFromWasm0(ptr1, len1);
    } finally {
        wasm.__wbindgen_free(deferred2_0, deferred2_1, 1);
    }
}

/**
 * The AI-backend base URL baked into this build by the `local-backend`
 * feature switch. Exposed so the frontend can report to Sentry if a
 * local-backend build ever ends up deployed to production.
 * @returns {string}
 */
export function get_ai_server_url() {
    let deferred2_0;
    let deferred2_1;
    try {
        const ret = wasm.get_ai_server_url();
        var ptr1 = ret[0];
        var len1 = ret[1];
        if (ret[3]) {
            ptr1 = 0; len1 = 0;
            throw takeFromExternrefTable0(ret[2]);
        }
        deferred2_0 = ptr1;
        deferred2_1 = len1;
        return getStringFromWasm0(ptr1, len1);
    } finally {
        wasm.__wbindgen_free(deferred2_0, deferred2_1, 1);
    }
}

/**
 * @returns {string}
 */
export function get_app_version() {
    let deferred2_0;
    let deferred2_1;
    try {
        const ret = wasm.get_app_version();
        var ptr1 = ret[0];
        var len1 = ret[1];
        if (ret[3]) {
            ptr1 = 0; len1 = 0;
            throw takeFromExternrefTable0(ret[2]);
        }
        deferred2_0 = ptr1;
        deferred2_1 = len1;
        return getStringFromWasm0(ptr1, len1);
    } finally {
        wasm.__wbindgen_free(deferred2_0, deferred2_1, 1);
    }
}

/**
 * @param {AudioRequest} request
 * @param {string | null} [access_token]
 * @returns {Promise<AudioResult>}
 */
export function get_audio(request, access_token) {
    const ret = wasm.get_audio(request, isLikeNone(access_token) ? 0 : addToExternrefTable0(access_token));
    return ret;
}

/**
 * Version counter of the local audio-clip mirror; changes whenever a clip
 * is cached or evicted. The frontend polls this to re-run challenge
 * selection as the background prefetcher lands clips (which can un-hide
 * audio challenges that were held back as not-yet-playable).
 * @returns {number}
 */
export function get_audio_cache_version() {
    const ret = wasm.get_audio_cache_version();
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return ret[0] >>> 0;
}

/**
 * @returns {Array<Course>}
 */
export function get_available_courses() {
    const ret = wasm.get_available_courses();
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return takeFromExternrefTable0(ret[0]);
}

/**
 * Timestamps are device-local host state. Expiry is evaluated with the host's
 * clock; an active review should finish before the host applies expired bans.
 * @param {number | null | undefined} listening_since
 * @param {number | null | undefined} speaking_since
 * @param {number} now_ms
 * @returns {ChallengeRestrictions}
 */
export function get_challenge_restrictions(listening_since, speaking_since, now_ms) {
    const ret = wasm.get_challenge_restrictions(isLikeNone(listening_since) ? 0 : addToExternrefTable0(listening_since), isLikeNone(speaking_since) ? 0 : addToExternrefTable0(speaking_since), now_ms);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return takeFromExternrefTable0(ret[0]);
}

/**
 * The movie clip for a sentence, if the loaded manifest lists one — from
 * the OPFS cache when possible, otherwise via the AI backend. `None` means
 * the sentence has no clip (a purely local answer, so calling this freely
 * is cheap).
 * @param {Language} language
 * @param {string} text
 * @param {string | null} [access_token]
 * @returns {Promise<(ClipResult | undefined)>}
 */
export function get_clip(language, text, access_token) {
    const ptr0 = passStringToWasm0(text, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.get_clip(language, ptr0, len0, isLikeNone(access_token) ? 0 : addToExternrefTable0(access_token));
    return ret;
}

/**
 * Bumped whenever a clip manifest loads or refreshes. The frontend polls
 * this (alongside the audio cache version) to re-run challenge selection
 * once clip knowledge arrives.
 * @returns {number}
 */
export function get_clip_manifest_version() {
    const ret = wasm.get_clip_manifest_version();
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return ret[0] >>> 0;
}

/**
 * @returns {Array<Course>}
 */
export function get_courses() {
    const ret = wasm.get_courses();
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return takeFromExternrefTable0(ret[0]);
}

/**
 * @returns {Array<DailyGoalOption>}
 */
export function get_daily_goal_options() {
    const ret = wasm.get_daily_goal_options();
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return takeFromExternrefTable0(ret[0]);
}

/**
 * `total_card_count` is the number of due + future cards, not lifetime reviews.
 * @param {number} total_card_count
 * @param {number} times_type_seen
 * @returns {FlashcardDisclosure}
 */
export function get_flashcard_disclosure(total_card_count, times_type_seen) {
    const ret = wasm.get_flashcard_disclosure(total_card_count, times_type_seen);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return takeFromExternrefTable0(ret[0]);
}

/**
 * @param {string | null | undefined} user_id
 * @param {string | null | undefined} slug
 * @param {string} access_token
 * @returns {Promise<FollowStatus>}
 */
export function get_follow_status(user_id, slug, access_token) {
    const ptr0 = passStringToWasm0(access_token, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.get_follow_status(isLikeNone(user_id) ? 0 : addToExternrefTable0(user_id), isLikeNone(slug) ? 0 : addToExternrefTable0(slug), ptr0, len0);
    return ret;
}

/**
 * @param {boolean} has_future_card
 * @param {number} cards_added
 * @param {number} smart_add_count
 * @returns {IdleStudyState}
 */
export function get_idle_study_state(has_future_card, cards_added, smart_add_count) {
    const ret = wasm.get_idle_study_state(has_future_card, cards_added, smart_add_count);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return takeFromExternrefTable0(ret[0]);
}

/**
 * @param {Language} language
 * @returns {LanguageMetadata}
 */
export function get_language_metadata(language) {
    const ret = wasm.get_language_metadata(language);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return takeFromExternrefTable0(ret[0]);
}

/**
 * A language name in the independent UI locale.
 * @param {Language} language
 * @returns {LanguageName}
 */
export function get_language_name(language) {
    const ret = wasm.get_language_name(language);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return takeFromExternrefTable0(ret[0]);
}

/**
 * @param {PlacementSession} session
 * @returns {PlacementSessionInfo}
 */
export function get_placement_session_info(session) {
    const ret = wasm.get_placement_session_info(session);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return takeFromExternrefTable0(ret[0]);
}

/**
 * @param {string} user_id
 * @returns {Promise<Profile>}
 */
export function get_profile_by_id(user_id) {
    const ptr0 = passStringToWasm0(user_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.get_profile_by_id(ptr0, len0);
    return ret;
}

/**
 * @param {string} slug
 * @returns {Promise<Profile>}
 */
export function get_profile_by_slug(slug) {
    const ptr0 = passStringToWasm0(slug, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.get_profile_by_slug(ptr0, len0);
    return ret;
}

/**
 * @param {SentenceListSelection | null | undefined} selection
 * @param {boolean} has_movies
 * @param {boolean} has_pimsleur
 * @returns {SentenceListNavigation}
 */
export function get_sentence_list_navigation(selection, has_movies, has_pimsleur) {
    const ret = wasm.get_sentence_list_navigation(isLikeNone(selection) ? 0 : addToExternrefTable0(selection), has_movies, has_pimsleur);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return takeFromExternrefTable0(ret[0]);
}

/**
 * @returns {Array<CourseShowcase>}
 */
export function get_showcase_data() {
    const ret = wasm.get_showcase_data();
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return takeFromExternrefTable0(ret[0]);
}

/**
 * @param {AudioRequest} request
 * @param {string | null} [access_token]
 * @returns {Promise<AudioResult>}
 */
export function get_temp_audio(request, access_token) {
    const ret = wasm.get_temp_audio(request, isLikeNone(access_token) ? 0 : addToExternrefTable0(access_token));
    return ret;
}

/**
 * @param {TranscribeComprehensibleSentence} challenge
 * @param {Array<PartGraded>} results
 * @returns {Array<ReviewDefinition>}
 */
export function get_transcription_review_definitions(challenge, results) {
    const ret = wasm.get_transcription_review_definitions(challenge, results);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return takeFromExternrefTable0(ret[0]);
}

/**
 * @param {TranslateComprehensibleSentence} sentence
 * @param {ManualTranslationGrade | null | undefined} grade
 * @param {boolean} is_perfect
 * @param {Uint32Array} tapped_words
 * @param {Language} language
 * @returns {TranslationReviewFeedback}
 */
export function get_translation_review_feedback(sentence, grade, is_perfect, tapped_words, language) {
    const ret = wasm.get_translation_review_feedback(sentence, isLikeNone(grade) ? 0 : addToExternrefTable0(grade), is_perfect, tapped_words, language);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return takeFromExternrefTable0(ret[0]);
}

/**
 * @param {string} user_id
 * @returns {Promise<Array<UserLanguageStats>>}
 */
export function get_user_language_stats_by_id(user_id) {
    const ptr0 = passStringToWasm0(user_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.get_user_language_stats_by_id(ptr0, len0);
    return ret;
}

/**
 * @param {string} slug
 * @returns {Promise<Array<UserLanguageStats>>}
 */
export function get_user_language_stats_by_slug(slug) {
    const ptr0 = passStringToWasm0(slug, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.get_user_language_stats_by_slug(ptr0, len0);
    return ret;
}

/**
 * @param {Gram<string>} gram
 * @param {Language} language
 * @returns {string}
 */
export function gram_to_display_string(gram, language) {
    let deferred2_0;
    let deferred2_1;
    try {
        const ret = wasm.gram_to_display_string(gram, language);
        var ptr1 = ret[0];
        var len1 = ret[1];
        if (ret[3]) {
            ptr1 = 0; len1 = 0;
            throw takeFromExternrefTable0(ret[2]);
        }
        deferred2_0 = ptr1;
        deferred2_1 = len1;
        return getStringFromWasm0(ptr1, len1);
    } finally {
        wasm.__wbindgen_free(deferred2_0, deferred2_1, 1);
    }
}

/**
 * @param {AudioRequest} request
 * @returns {Promise<void>}
 */
export function invalidate_audio_cache(request) {
    const ret = wasm.invalidate_audio_cache(request);
    return ret;
}

/**
 * Forget everything cached for this sentence's clip, so the next `get_clip`
 * re-asks the server. Called when the video element rejects cached bytes.
 * @param {Language} language
 * @param {string} text
 * @returns {Promise<void>}
 */
export function invalidate_clip_cache(language, text) {
    const ptr0 = passStringToWasm0(text, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.invalidate_clip_cache(language, ptr0, len0);
    return ret;
}

/**
 * Hosts scope drafts as `<user id>:<course>`, so an account's drafts can be
 * found for deletion across every course.
 * @param {string} key
 * @param {string} user_id
 * @returns {boolean}
 */
export function is_pending_review_key_for_user(key, user_id) {
    const ptr0 = passStringToWasm0(key, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passStringToWasm0(user_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len1 = WASM_VECTOR_LEN;
    const ret = wasm.is_pending_review_key_for_user(ptr0, len0, ptr1, len1);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return ret[0] !== 0;
}

/**
 * @param {Language} language
 * @param {Language} native_language
 * @returns {Promise<BooksScreenView>}
 */
export function load_books(language, native_language) {
    const ret = wasm.load_books(language, native_language);
    return ret;
}

/**
 * All displayable readings, with agreement kept together per alternative.
 * @param {Array<Morphology>} morphology
 * @returns {string}
 */
export function morphology_label(morphology) {
    let deferred2_0;
    let deferred2_1;
    try {
        const ret = wasm.morphology_label(morphology);
        var ptr1 = ret[0];
        var len1 = ret[1];
        if (ret[3]) {
            ptr1 = 0; len1 = 0;
            throw takeFromExternrefTable0(ret[2]);
        }
        deferred2_0 = ptr1;
        deferred2_1 = len1;
        return getStringFromWasm0(ptr1, len1);
    } finally {
        wasm.__wbindgen_free(deferred2_0, deferred2_1, 1);
    }
}

/**
 * @param {number} current
 * @param {number} projected
 * @returns {(number | undefined)}
 */
export function next_progress_milestone(current, projected) {
    const ret = wasm.next_progress_milestone(current, projected);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return takeFromExternrefTable0(ret[0]);
}

/**
 * "You'll review <word> 2 days from now." for a written card, otherwise
 * "Your next review is 2 days from now." (web copy). Hosts re-ask at
 * `refresh_at_ms` so the countdown stays live without polling; the words and
 * rounding are Rust's so both platforms agree.
 * @param {CardSummary} card
 * @param {number} now_ms
 * @returns {LiveText}
 */
export function next_review_line(card, now_ms) {
    const ret = wasm.next_review_line(card, now_ms);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return takeFromExternrefTable0(ret[0]);
}

/**
 * @param {OnboardingState} state
 * @param {OnboardingEvent} event
 * @returns {OnboardingTransition}
 */
export function onboarding_reduce(state, event) {
    const ret = wasm.onboarding_reduce(state, event);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return takeFromExternrefTable0(ret[0]);
}

/**
 * @param {Language} target_language
 * @param {boolean} has_heard_about
 * @param {boolean} offer_notifications
 * @param {OnboardingPurpose} purpose
 * @returns {OnboardingState}
 */
export function onboarding_start(target_language, has_heard_about, offer_notifications, purpose) {
    const ret = wasm.onboarding_start(target_language, has_heard_about, offer_notifications, purpose);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return takeFromExternrefTable0(ret[0]);
}

/**
 * @param {OnboardingState} state
 * @returns {OnboardingView}
 */
export function onboarding_view(state) {
    const ret = wasm.onboarding_view(state);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return takeFromExternrefTable0(ret[0]);
}

/**
 * @param {Array<Part>} parts
 * @param {Array<TranscriptionInput>} inputs
 * @returns {TranscriptionSubmission}
 */
export function prepare_transcription_submission(parts, inputs) {
    const ret = wasm.prepare_transcription_submission(parts, inputs);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return takeFromExternrefTable0(ret[0]);
}

/**
 * @param {Array<Literal<string>>} literals
 * @param {AutoGradeTranslationResponse} response
 * @returns {TranslationReviewResult}
 */
export function prepare_translation_review(literals, response) {
    const ret = wasm.prepare_translation_review(literals, response);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return takeFromExternrefTable0(ret[0]);
}

/**
 * @param {string} pattern
 * @param {PronunciationGuide} guide
 * @param {Array<PronunciationCue>} cues
 * @param {boolean} is_new
 * @param {number} times_type_seen
 * @param {Language} target_language
 * @returns {PronunciationView}
 */
export function pronunciation_view(pattern, guide, cues, is_new, times_type_seen, target_language) {
    const ptr0 = passStringToWasm0(pattern, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.pronunciation_view(ptr0, len0, guide, cues, is_new, times_type_seen, target_language);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return takeFromExternrefTable0(ret[0]);
}

/**
 * `text_size` is a step the host saved earlier, or `None` for the default.
 * @param {number} width
 * @param {number} height
 * @param {number | null} [text_size]
 * @returns {ReaderLayout}
 */
export function reader_layout(width, height, text_size) {
    const ret = wasm.reader_layout(width, height, isLikeNone(text_size) ? 0 : addToExternrefTable0(text_size));
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return takeFromExternrefTable0(ret[0]);
}

/**
 * @param {boolean} dark
 * @returns {Array<ReaderTheme>}
 */
export function reader_themes(dark) {
    const ret = wasm.reader_themes(dark);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return takeFromExternrefTable0(ret[0]);
}

/**
 * Fetch the latest clip manifest for a language from the AI backend and
 * persist it, so challenge selection knows which sentences have movie
 * clips. The frontend calls this in the background on load; until it (or
 * the OPFS-cached manifest from a previous session) lands, the app simply
 * behaves as if no sentences have clips — nothing blocks on it.
 * @param {Language} language
 * @param {string | null} [access_token]
 * @returns {Promise<void>}
 */
export function refresh_clip_manifest(language, access_token) {
    const ret = wasm.refresh_clip_manifest(language, isLikeNone(access_token) ? 0 : addToExternrefTable0(access_token));
    return ret;
}

/**
 * Stores a report in Supabase's `issues` table as the signed-in learner.
 * @param {Language} language
 * @param {IssueSubject} subject
 * @param {string} issue
 * @param {string} user_id
 * @param {string} access_token
 * @returns {Promise<void>}
 */
export function report_issue(language, subject, issue, user_id, access_token) {
    const ptr0 = passStringToWasm0(issue, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passStringToWasm0(user_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len1 = WASM_VECTOR_LEN;
    const ptr2 = passStringToWasm0(access_token, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len2 = WASM_VECTOR_LEN;
    const ret = wasm.report_issue(language, subject, ptr0, len0, ptr1, len1, ptr2, len2);
    return ret;
}

/**
 * @returns {ReportIssueCopy}
 */
export function report_issue_copy() {
    const ret = wasm.report_issue_copy();
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return takeFromExternrefTable0(ret[0]);
}

/**
 * @param {string} tag
 */
export function set_ui_language(tag) {
    const ptr0 = passStringToWasm0(tag, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.set_ui_language(ptr0, len0);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return takeFromExternrefTable0(ret[0]);
}

/**
 * @param {number} times_type_seen
 * @returns {boolean}
 */
export function should_show_challenge_tutorial(times_type_seen) {
    const ret = wasm.should_show_challenge_tutorial(times_type_seen);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return ret[0] !== 0;
}

/**
 * @returns {SupabaseConfig}
 */
export function supabase_config() {
    const ret = wasm.supabase_config();
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return takeFromExternrefTable0(ret[0]);
}

/**
 * @param {SyncStatusInputs} inputs
 * @returns {SyncStatusView}
 */
export function sync_status_view(inputs) {
    const ret = wasm.sync_status_view(inputs);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return takeFromExternrefTable0(ret[0]);
}

/**
 * @returns {Promise<boolean>}
 */
export function test_opfs() {
    const ret = wasm.test_opfs();
    return ret;
}

/**
 * @param {PlacementSession} session
 * @param {string} word
 * @returns {PlacementSession}
 */
export function toggle_placement_word(session, word) {
    const ptr0 = passStringToWasm0(word, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.toggle_placement_word(session, ptr0, len0);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return takeFromExternrefTable0(ret[0]);
}

/**
 * @param {Array<PartGraded>} results
 * @returns {boolean}
 */
export function transcription_is_perfect(results) {
    const ret = wasm.transcription_is_perfect(results);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return ret[0] !== 0;
}

/**
 * @param {string} cue_text
 * @param {string} sentence
 * @param {string} masked
 * @returns {string}
 */
export function transcription_mask_cue(cue_text, sentence, masked) {
    let deferred5_0;
    let deferred5_1;
    try {
        const ptr0 = passStringToWasm0(cue_text, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len0 = WASM_VECTOR_LEN;
        const ptr1 = passStringToWasm0(sentence, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len1 = WASM_VECTOR_LEN;
        const ptr2 = passStringToWasm0(masked, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
        const len2 = WASM_VECTOR_LEN;
        const ret = wasm.transcription_mask_cue(ptr0, len0, ptr1, len1, ptr2, len2);
        var ptr4 = ret[0];
        var len4 = ret[1];
        if (ret[3]) {
            ptr4 = 0; len4 = 0;
            throw takeFromExternrefTable0(ret[2]);
        }
        deferred5_0 = ptr4;
        deferred5_1 = len4;
        return getStringFromWasm0(ptr4, len4);
    } finally {
        wasm.__wbindgen_free(deferred5_0, deferred5_1, 1);
    }
}

/**
 * @param {Array<Part>} parts
 * @param {string} scope
 * @param {string} app_version
 * @param {bigint} review_count
 * @returns {PendingReviewSlot}
 */
export function transcription_pending_slot(parts, scope, app_version, review_count) {
    const ptr0 = passStringToWasm0(scope, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passStringToWasm0(app_version, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len1 = WASM_VECTOR_LEN;
    const ret = wasm.transcription_pending_slot(parts, ptr0, len0, ptr1, len1, review_count);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return takeFromExternrefTable0(ret[0]);
}

/**
 * @param {TranscriptionState} state
 * @returns {TranscriptionStep}
 */
export function transcription_resume(state) {
    const ret = wasm.transcription_resume(state);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return takeFromExternrefTable0(ret[0]);
}

/**
 * Chunk the displayed parts, retaining part indices for input events. A blank
 * occupies one token; its outer boundaries come from its first/last literals.
 * @param {Array<Part>} parts
 * @param {Language} language
 * @returns {Array<SentenceChunk>}
 */
export function transcription_sentence_chunks(parts, language) {
    const ret = wasm.transcription_sentence_chunks(parts, language);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return takeFromExternrefTable0(ret[0]);
}

/**
 * @param {Array<Array<RubySegment>>} literal_ruby
 * @param {Array<Part>} parts
 * @param {Array<[string, ProperNounDefinition]>} proper_noun_definitions
 * @returns {TranscriptionState}
 */
export function transcription_start(literal_ruby, parts, proper_noun_definitions) {
    const ret = wasm.transcription_start(literal_ruby, parts, proper_noun_definitions);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return takeFromExternrefTable0(ret[0]);
}

/**
 * @param {TranscriptionState} state
 * @param {TranscriptionEvent} event
 * @param {Language} target_language
 * @returns {TranscriptionStep}
 */
export function transcription_transition(state, event, target_language) {
    const ret = wasm.transcription_transition(state, event, target_language);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return takeFromExternrefTable0(ret[0]);
}

/**
 * @param {TranscriptionState} state
 * @returns {TranscriptionView}
 */
export function transcription_view(state) {
    const ret = wasm.transcription_view(state);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return takeFromExternrefTable0(ret[0]);
}

/**
 * Whether an autograde response should count the whole sentence as
 * perfectly translated: no phrase forgotten, every heteronym affirmatively
 * graded Remembered, and a real (non-heuristic) grading. An indeterminate
 * or missing grade for a heteronym is not perfect — promoting that would
 * credit a word the user never demonstrated. Shared by the app's
 * TranslationChallenge and yap-mcp's grade_translation so the promotion
 * rule lives in exactly one place.
 * @param {Array<Literal<string>>} literals
 * @param {AutoGradeTranslationResponse} response
 * @returns {boolean}
 */
export function translation_is_perfect(literals, response) {
    const ret = wasm.translation_is_perfect(literals, response);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return ret[0] !== 0;
}

/**
 * @param {TranslateComprehensibleSentence} sentence
 * @param {string} scope
 * @param {string} app_version
 * @param {bigint} review_count
 * @returns {PendingReviewSlot}
 */
export function translation_pending_slot(sentence, scope, app_version, review_count) {
    const ptr0 = passStringToWasm0(scope, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passStringToWasm0(app_version, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len1 = WASM_VECTOR_LEN;
    const ret = wasm.translation_pending_slot(sentence, ptr0, len0, ptr1, len1, review_count);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return takeFromExternrefTable0(ret[0]);
}

/**
 * @param {TranslationState} state
 * @returns {TranslationStep}
 */
export function translation_resume(state) {
    const ret = wasm.translation_resume(state);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return takeFromExternrefTable0(ret[0]);
}

/**
 * @param {TranslateComprehensibleSentence} sentence
 * @param {Course} course
 * @returns {TranslationState}
 */
export function translation_start(sentence, course) {
    const ret = wasm.translation_start(sentence, course);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return takeFromExternrefTable0(ret[0]);
}

/**
 * @param {TranslationState} state
 * @param {TranslationEvent} event
 * @returns {TranslationStep}
 */
export function translation_transition(state, event) {
    const ret = wasm.translation_transition(state, event);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return takeFromExternrefTable0(ret[0]);
}

/**
 * @param {TranslationState} state
 * @returns {TranslationView}
 */
export function translation_view(state) {
    const ret = wasm.translation_view(state);
    if (ret[2]) {
        throw takeFromExternrefTable0(ret[1]);
    }
    return takeFromExternrefTable0(ret[0]);
}

/**
 * @returns {string}
 */
export function ui_language() {
    let deferred2_0;
    let deferred2_1;
    try {
        const ret = wasm.ui_language();
        var ptr1 = ret[0];
        var len1 = ret[1];
        if (ret[3]) {
            ptr1 = 0; len1 = 0;
            throw takeFromExternrefTable0(ret[2]);
        }
        deferred2_0 = ptr1;
        deferred2_1 = len1;
        return getStringFromWasm0(ptr1, len1);
    } finally {
        wasm.__wbindgen_free(deferred2_0, deferred2_1, 1);
    }
}

/**
 * @param {string} user_id
 * @param {string} access_token
 * @returns {Promise<FollowResponse>}
 */
export function unfollow_user(user_id, access_token) {
    const ptr0 = passStringToWasm0(user_id, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ptr1 = passStringToWasm0(access_token, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len1 = WASM_VECTOR_LEN;
    const ret = wasm.unfollow_user(ptr0, len0, ptr1, len1);
    return ret;
}

/**
 * @param {string | null | undefined} display_name
 * @param {string | null | undefined} bio
 * @param {string} access_token
 * @returns {Promise<UpdateProfileResponse>}
 */
export function update_profile(display_name, bio, access_token) {
    const ptr0 = passStringToWasm0(access_token, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len0 = WASM_VECTOR_LEN;
    const ret = wasm.update_profile(isLikeNone(display_name) ? 0 : addToExternrefTable0(display_name), isLikeNone(bio) ? 0 : addToExternrefTable0(bio), ptr0, len0);
    return ret;
}
export function __wbg_Error_ef53bc310eb298a0(arg0, arg1) {
    const ret = Error(getStringFromWasm0(arg0, arg1));
    return ret;
}
export function __wbg_Number_6b506e6536831eaa(arg0) {
    const ret = Number(arg0);
    return ret;
}
export function __wbg_String_8564e559799eccda(arg0, arg1) {
    const ret = String(arg1);
    const ptr1 = passStringToWasm0(ret, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len1 = WASM_VECTOR_LEN;
    getDataViewMemory0().setInt32(arg0 + 4 * 1, len1, true);
    getDataViewMemory0().setInt32(arg0 + 4 * 0, ptr1, true);
}
export function __wbg___wbindgen_bigint_get_as_i64_38130e98eecd467d(arg0, arg1) {
    const v = arg1;
    const ret = typeof(v) === 'bigint' ? v : undefined;
    getDataViewMemory0().setBigInt64(arg0 + 8 * 1, isLikeNone(ret) ? BigInt(0) : ret, true);
    getDataViewMemory0().setInt32(arg0 + 4 * 0, !isLikeNone(ret), true);
}
export function __wbg___wbindgen_boolean_get_1a45e2c38d4d41b9(arg0) {
    const v = arg0;
    const ret = typeof(v) === 'boolean' ? v : undefined;
    return isLikeNone(ret) ? 0xFFFFFF : ret ? 1 : 0;
}
export function __wbg___wbindgen_debug_string_0accd80f45e5faa2(arg0, arg1) {
    const ret = debugString(arg1);
    const ptr1 = passStringToWasm0(ret, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len1 = WASM_VECTOR_LEN;
    getDataViewMemory0().setInt32(arg0 + 4 * 1, len1, true);
    getDataViewMemory0().setInt32(arg0 + 4 * 0, ptr1, true);
}
export function __wbg___wbindgen_in_70a403a56e771704(arg0, arg1) {
    const ret = arg0 in arg1;
    return ret;
}
export function __wbg___wbindgen_is_bigint_6ffd6468a9bc44b9(arg0) {
    const ret = typeof(arg0) === 'bigint';
    return ret;
}
export function __wbg___wbindgen_is_function_754e9f305ff6029e(arg0) {
    const ret = typeof(arg0) === 'function';
    return ret;
}
export function __wbg___wbindgen_is_null_87c3bfe968c6a5ad(arg0) {
    const ret = arg0 === null;
    return ret;
}
export function __wbg___wbindgen_is_object_56732c2bc353f41d(arg0) {
    const val = arg0;
    const ret = typeof(val) === 'object' && val !== null;
    return ret;
}
export function __wbg___wbindgen_is_string_c236cabd84a4d769(arg0) {
    const ret = typeof(arg0) === 'string';
    return ret;
}
export function __wbg___wbindgen_is_undefined_67b456be8673d3d7(arg0) {
    const ret = arg0 === undefined;
    return ret;
}
export function __wbg___wbindgen_jsval_eq_1068e624fa87f6ab(arg0, arg1) {
    const ret = arg0 === arg1;
    return ret;
}
export function __wbg___wbindgen_jsval_loose_eq_2c56564c75129511(arg0, arg1) {
    const ret = arg0 == arg1;
    return ret;
}
export function __wbg___wbindgen_number_get_9bb1761122181af2(arg0, arg1) {
    const obj = arg1;
    const ret = typeof(obj) === 'number' ? obj : undefined;
    getDataViewMemory0().setFloat64(arg0 + 8 * 1, isLikeNone(ret) ? 0 : ret, true);
    getDataViewMemory0().setInt32(arg0 + 4 * 0, !isLikeNone(ret), true);
}
export function __wbg___wbindgen_string_get_72bdf95d3ae505b1(arg0, arg1) {
    const obj = arg1;
    const ret = typeof(obj) === 'string' ? obj : undefined;
    var ptr1 = isLikeNone(ret) ? 0 : passStringToWasm0(ret, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    var len1 = WASM_VECTOR_LEN;
    getDataViewMemory0().setInt32(arg0 + 4 * 1, len1, true);
    getDataViewMemory0().setInt32(arg0 + 4 * 0, ptr1, true);
}
export function __wbg___wbindgen_throw_1506f2235d1bdba0(arg0, arg1) {
    throw new Error(getStringFromWasm0(arg0, arg1));
}
export function __wbg__wbg_cb_unref_61db23ac97f16c31(arg0) {
    arg0._wbg_cb_unref();
}
export function __wbg_abort_2ec46222bf378517(arg0) {
    arg0.abort();
}
export function __wbg_aborted_3bd851834eb1ecce(arg0) {
    const ret = arg0.aborted;
    return ret;
}
export function __wbg_addEventListener_5593b0efd622abd6() { return handleError(function (arg0, arg1, arg2, arg3, arg4) {
    arg0.addEventListener(getStringFromWasm0(arg1, arg2), arg3, arg4);
}, arguments); }
export function __wbg_arrayBuffer_05927079aabe6d46() { return handleError(function (arg0) {
    const ret = arg0.arrayBuffer();
    return ret;
}, arguments); }
export function __wbg_arrayBuffer_a0e88fd0c0e099b2(arg0) {
    const ret = arg0.arrayBuffer();
    return ret;
}
export function __wbg_audioresult_new(arg0) {
    const ret = AudioResult.__wrap(arg0);
    return ret;
}
export function __wbg_body_61a0827da5b6d2bc(arg0) {
    const ret = arg0.body;
    return isLikeNone(ret) ? 0 : addToExternrefTable0(ret);
}
export function __wbg_call_40e4174f169eaca7() { return handleError(function (arg0, arg1, arg2, arg3) {
    const ret = arg0.call(arg1, arg2, arg3);
    return ret;
}, arguments); }
export function __wbg_call_8a89609d89f6608a() { return handleError(function (arg0, arg1) {
    const ret = arg0.call(arg1);
    return ret;
}, arguments); }
export function __wbg_call_9c758de292015997() { return handleError(function (arg0, arg1, arg2) {
    const ret = arg0.call(arg1, arg2);
    return ret;
}, arguments); }
export function __wbg_catch_17ae9c6dfb88ad8a(arg0, arg1) {
    const ret = arg0.catch(arg1);
    return ret;
}
export function __wbg_clearTimeout_113b1cde814ec762(arg0) {
    const ret = clearTimeout(arg0);
    return ret;
}
export function __wbg_clipresult_new(arg0) {
    const ret = ClipResult.__wrap(arg0);
    return ret;
}
export function __wbg_close_005f131e35bfd850(arg0) {
    arg0.close();
}
export function __wbg_close_3a6bb6c95fcc0a4a(arg0) {
    const ret = arg0.close();
    return ret;
}
export function __wbg_createWritable_c0ea35f4b8856093(arg0, arg1) {
    const ret = arg0.createWritable(arg1);
    return ret;
}
export function __wbg_debug_6d96d354ecb8cdb3(arg0, arg1, arg2, arg3) {
    console.debug(arg0, arg1, arg2, arg3);
}
export function __wbg_deck_new(arg0) {
    const ret = Deck.__wrap(arg0);
    return ret;
}
export function __wbg_deref_4e4220ad43c8ba63(arg0) {
    const ret = arg0.deref();
    return isLikeNone(ret) ? 0 : addToExternrefTable0(ret);
}
export function __wbg_dictionaryword_new(arg0) {
    const ret = DictionaryWord.__wrap(arg0);
    return ret;
}
export function __wbg_done_60cf307fcc680536(arg0) {
    const ret = arg0.done;
    return ret;
}
export function __wbg_entries_04b37a02507f1713(arg0) {
    const ret = Object.entries(arg0);
    return ret;
}
export function __wbg_entries_c4d0a7d1625aea7f(arg0) {
    const ret = arg0.entries();
    return ret;
}
export function __wbg_error_78ff5b3a29b770e0(arg0) {
    console.error(arg0);
}
export function __wbg_error_9ad1450feb5d541d(arg0, arg1, arg2, arg3) {
    console.error(arg0, arg1, arg2, arg3);
}
export function __wbg_error_a6fa202b58aa1cd3(arg0, arg1) {
    let deferred0_0;
    let deferred0_1;
    try {
        deferred0_0 = arg0;
        deferred0_1 = arg1;
        console.error(getStringFromWasm0(arg0, arg1));
    } finally {
        wasm.__wbindgen_free(deferred0_0, deferred0_1, 1);
    }
}
export function __wbg_fetch_ce1af4d1b59a60be(arg0, arg1) {
    const ret = arg0.fetch(arg1);
    return ret;
}
export function __wbg_from_d300fe49deab18f5(arg0) {
    const ret = Array.from(arg0);
    return ret;
}
export function __wbg_getDirectoryHandle_1074878dbbebce4d(arg0, arg1, arg2, arg3) {
    const ret = arg0.getDirectoryHandle(getStringFromWasm0(arg1, arg2), arg3);
    return ret;
}
export function __wbg_getDirectory_81f0c16f73a7f542(arg0) {
    const ret = arg0.getDirectory();
    return ret;
}
export function __wbg_getFileHandle_1bfaf7268935b051(arg0, arg1, arg2, arg3) {
    const ret = arg0.getFileHandle(getStringFromWasm0(arg1, arg2), arg3);
    return ret;
}
export function __wbg_getFile_57f23539bd8b1536(arg0) {
    const ret = arg0.getFile();
    return ret;
}
export function __wbg_getReader_186390151a19bc55(arg0) {
    const ret = arg0.getReader();
    return ret;
}
export function __wbg_getTime_00b3f7db575e4ef5(arg0) {
    const ret = arg0.getTime();
    return ret;
}
export function __wbg_getTimezoneOffset_08e2892156231088(arg0) {
    const ret = arg0.getTimezoneOffset();
    return ret;
}
export function __wbg_get_1f8f054ddbaa7db2() { return handleError(function (arg0, arg1) {
    const ret = Reflect.get(arg0, arg1);
    return ret;
}, arguments); }
export function __wbg_get_2b48c7d0d006a781(arg0, arg1) {
    const ret = arg0[arg1 >>> 0];
    return ret;
}
export function __wbg_get_de6a0f7d4d18a304() { return handleError(function (arg0, arg1) {
    const ret = Reflect.get(arg0, arg1);
    return ret;
}, arguments); }
export function __wbg_get_unchecked_33f6e5c9e2f2d6b2(arg0, arg1) {
    const ret = arg0[arg1 >>> 0];
    return ret;
}
export function __wbg_get_with_ref_key_6412cf3094599694(arg0, arg1) {
    const ret = arg0[arg1];
    return ret;
}
export function __wbg_headers_d94793faa2079ffa(arg0) {
    const ret = arg0.headers;
    return ret;
}
export function __wbg_info_5cfb3f6c22c53cf9(arg0, arg1, arg2, arg3) {
    console.info(arg0, arg1, arg2, arg3);
}
export function __wbg_instanceof_AbortSignal_62ae3458aa4f4cc6(arg0) {
    let result;
    try {
        result = arg0 instanceof AbortSignal;
    } catch (_) {
        result = false;
    }
    const ret = result;
    return ret;
}
export function __wbg_instanceof_ArrayBuffer_8f49811467741499(arg0) {
    let result;
    try {
        result = arg0 instanceof ArrayBuffer;
    } catch (_) {
        result = false;
    }
    const ret = result;
    return ret;
}
export function __wbg_instanceof_Error_94c8c9d9e410014a(arg0) {
    let result;
    try {
        result = arg0 instanceof Error;
    } catch (_) {
        result = false;
    }
    const ret = result;
    return ret;
}
export function __wbg_instanceof_FileSystemDirectoryHandle_527d4a4ebcd0f631(arg0) {
    let result;
    try {
        result = arg0 instanceof FileSystemDirectoryHandle;
    } catch (_) {
        result = false;
    }
    const ret = result;
    return ret;
}
export function __wbg_instanceof_FileSystemFileHandle_6290c6738a00655e(arg0) {
    let result;
    try {
        result = arg0 instanceof FileSystemFileHandle;
    } catch (_) {
        result = false;
    }
    const ret = result;
    return ret;
}
export function __wbg_instanceof_Map_9fc06d9a951bcee6(arg0) {
    let result;
    try {
        result = arg0 instanceof Map;
    } catch (_) {
        result = false;
    }
    const ret = result;
    return ret;
}
export function __wbg_instanceof_ReadableStreamDefaultReader_7d8cc44877a9f6ef(arg0) {
    let result;
    try {
        result = arg0 instanceof ReadableStreamDefaultReader;
    } catch (_) {
        result = false;
    }
    const ret = result;
    return ret;
}
export function __wbg_instanceof_Response_cb984bd66d7bd408(arg0) {
    let result;
    try {
        result = arg0 instanceof Response;
    } catch (_) {
        result = false;
    }
    const ret = result;
    return ret;
}
export function __wbg_instanceof_Uint8Array_86f30649f63ef9c2(arg0) {
    let result;
    try {
        result = arg0 instanceof Uint8Array;
    } catch (_) {
        result = false;
    }
    const ret = result;
    return ret;
}
export function __wbg_instanceof_Window_e093be59ee9a8e14(arg0) {
    let result;
    try {
        result = arg0 instanceof Window;
    } catch (_) {
        result = false;
    }
    const ret = result;
    return ret;
}
export function __wbg_isArray_67c2c9c4313f4448(arg0) {
    const ret = Array.isArray(arg0);
    return ret;
}
export function __wbg_isSafeInteger_66acec27e09e99a7(arg0) {
    const ret = Number.isSafeInteger(arg0);
    return ret;
}
export function __wbg_isView_7196124a1d67a99a(arg0) {
    const ret = ArrayBuffer.isView(arg0);
    return ret;
}
export function __wbg_iterator_8732428d309e270e() {
    const ret = Symbol.iterator;
    return ret;
}
export function __wbg_length_4a591ecaa01354d9(arg0) {
    const ret = arg0.length;
    return ret;
}
export function __wbg_length_66f1a4b2e9026940(arg0) {
    const ret = arg0.length;
    return ret;
}
export function __wbg_listenerkey_new(arg0) {
    const ret = ListenerKey.__wrap(arg0);
    return ret;
}
export function __wbg_listenerkey_unwrap(arg0) {
    const ret = ListenerKey.__unwrap(arg0);
    return ret;
}
export function __wbg_lockupoffer_new(arg0) {
    const ret = LockupOffer.__wrap(arg0);
    return ret;
}
export function __wbg_log_d5e0f90a3ac097e3(arg0, arg1, arg2, arg3) {
    console.log(arg0, arg1, arg2, arg3);
}
export function __wbg_name_2e3d97e5d7abee6d(arg0) {
    const ret = arg0.name;
    return ret;
}
export function __wbg_navigator_3833ecdbc19d2757(arg0) {
    const ret = arg0.navigator;
    return ret;
}
export function __wbg_new_0_445c13a750296eb6() {
    const ret = new Date();
    return ret;
}
export function __wbg_new_0d09705104e164af() { return handleError(function () {
    const ret = new AbortController();
    return ret;
}, arguments); }
export function __wbg_new_227d7c05414eb861() {
    const ret = new Error();
    return ret;
}
export function __wbg_new_50bb5ebeecef71a8(arg0, arg1) {
    const ret = new Error(getStringFromWasm0(arg0, arg1));
    return ret;
}
export function __wbg_new_578aeef4b6b94378(arg0) {
    const ret = new Uint8Array(arg0);
    return ret;
}
export function __wbg_new_622fc80556be2e26() {
    const ret = new Map();
    return ret;
}
export function __wbg_new_69240efb3ceaa732(arg0) {
    const ret = new FinalizationRegistry(arg0);
    return ret;
}
export function __wbg_new_6a349535e20799b9(arg0) {
    const ret = new WeakRef(arg0);
    return ret;
}
export function __wbg_new_b682b81e8eaaf027(arg0, arg1) {
    try {
        var state0 = {a: arg0, b: arg1};
        var cb0 = (arg0, arg1) => {
            const a = state0.a;
            state0.a = 0;
            try {
                return wasm_bindgen__convert__closures_____invoke__h0a7cf1e297598b08(a, state0.b, arg0, arg1);
            } finally {
                state0.a = a;
            }
        };
        const ret = new Promise(cb0);
        return ret;
    } finally {
        state0.a = 0;
    }
}
export function __wbg_new_ce1ab61c1c2b300d() {
    const ret = new Object();
    return ret;
}
export function __wbg_new_d90091b82fdf5b91() {
    const ret = new Array();
    return ret;
}
export function __wbg_new_f6da38a590760568() { return handleError(function (arg0, arg1) {
    const ret = new BroadcastChannel(getStringFromWasm0(arg0, arg1));
    return ret;
}, arguments); }
export function __wbg_new_from_slice_18fa1f71286d66b8(arg0, arg1) {
    const ret = new Uint8Array(getArrayU8FromWasm0(arg0, arg1));
    return ret;
}
export function __wbg_new_typed_bf31d18f92484486(arg0, arg1) {
    try {
        var state0 = {a: arg0, b: arg1};
        var cb0 = (arg0, arg1) => {
            const a = state0.a;
            state0.a = 0;
            try {
                return wasm_bindgen__convert__closures_____invoke__h0a7cf1e297598b08(a, state0.b, arg0, arg1);
            } finally {
                state0.a = a;
            }
        };
        const ret = new Promise(cb0);
        return ret;
    } finally {
        state0.a = 0;
    }
}
export function __wbg_new_with_length_36a4998e27b014c5(arg0) {
    const ret = new Uint8Array(arg0 >>> 0);
    return ret;
}
export function __wbg_new_with_str_and_init_bcd02b79a793d27f() { return handleError(function (arg0, arg1, arg2) {
    const ret = new Request(getStringFromWasm0(arg0, arg1), arg2);
    return ret;
}, arguments); }
export function __wbg_new_with_u8_array_sequence_2cd9a3f01fcff785() { return handleError(function (arg0, arg1, arg2) {
    const ret = new File(arg0, getStringFromWasm0(arg1, arg2));
    return ret;
}, arguments); }
export function __wbg_next_9e03acdf51c4960d(arg0) {
    const ret = arg0.next;
    return ret;
}
export function __wbg_next_eb8ca7351fa27906() { return handleError(function (arg0) {
    const ret = arg0.next();
    return ret;
}, arguments); }
export function __wbg_next_fe4491d95e72b54d() { return handleError(function (arg0) {
    const ret = arg0.next();
    return ret;
}, arguments); }
export function __wbg_now_e7c6795a7f81e10f(arg0) {
    const ret = arg0.now();
    return ret;
}
export function __wbg_ok_fb13c30bc1893039(arg0) {
    const ret = arg0.ok;
    return ret;
}
export function __wbg_performance_3fcf6e32a7e1ed0a(arg0) {
    const ret = arg0.performance;
    return ret;
}
export function __wbg_postMessage_78111ac355397c0c() { return handleError(function (arg0, arg1) {
    arg0.postMessage(arg1);
}, arguments); }
export function __wbg_prototypesetcall_3249fc62a0fafa30(arg0, arg1, arg2) {
    Uint8Array.prototype.set.call(getArrayU8FromWasm0(arg0, arg1), arg2);
}
export function __wbg_push_a6822215aa43e71c(arg0, arg1) {
    const ret = arg0.push(arg1);
    return ret;
}
export function __wbg_queueMicrotask_35c611f4a14830b2(arg0) {
    queueMicrotask(arg0);
}
export function __wbg_queueMicrotask_404ed0a58e0b63cc(arg0) {
    const ret = arg0.queueMicrotask;
    return ret;
}
export function __wbg_randomUUID_04ea01e1c13ecfa1(arg0) {
    const ret = self.crypto.randomUUID();
    const ptr1 = passStringToWasm0(ret, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len1 = WASM_VECTOR_LEN;
    getDataViewMemory0().setInt32(arg0 + 4 * 1, len1, true);
    getDataViewMemory0().setInt32(arg0 + 4 * 0, ptr1, true);
}
export function __wbg_read_282e152a24fd0856(arg0) {
    const ret = arg0.read();
    return ret;
}
export function __wbg_register_2b38856410c0745e(arg0, arg1, arg2) {
    arg0.register(arg1, arg2);
}
export function __wbg_releaseoffer_new(arg0) {
    const ret = ReleaseOffer.__wrap(arg0);
    return ret;
}
export function __wbg_removeEntry_8c83cbf5446bf554(arg0, arg1, arg2, arg3) {
    const ret = arg0.removeEntry(getStringFromWasm0(arg1, arg2), arg3);
    return ret;
}
export function __wbg_removeEntry_e4b1351d33d6ed6e(arg0, arg1, arg2) {
    const ret = arg0.removeEntry(getStringFromWasm0(arg1, arg2));
    return ret;
}
export function __wbg_removeEventListener_9e2e49dbe3ca4858() { return handleError(function (arg0, arg1, arg2, arg3, arg4) {
    arg0.removeEventListener(getStringFromWasm0(arg1, arg2), arg3, arg4 !== 0);
}, arguments); }
export function __wbg_request_9ec6302d6583b381(arg0, arg1, arg2, arg3, arg4) {
    const ret = arg0.request(getStringFromWasm0(arg1, arg2), arg3, arg4);
    return ret;
}
export function __wbg_resolve_25a7e548d5881dca(arg0) {
    const ret = Promise.resolve(arg0);
    return ret;
}
export function __wbg_seek_d9a86a0fbd023a65() { return handleError(function (arg0, arg1) {
    const ret = arg0.seek(arg1 >>> 0);
    return ret;
}, arguments); }
export function __wbg_setTimeout_ef24d2fc3ad97385() { return handleError(function (arg0, arg1) {
    const ret = setTimeout(arg0, arg1);
    return ret;
}, arguments); }
export function __wbg_set_25ef40a9aeff260d() { return handleError(function (arg0, arg1, arg2, arg3, arg4) {
    arg0.set(getStringFromWasm0(arg1, arg2), getStringFromWasm0(arg3, arg4));
}, arguments); }
export function __wbg_set_52b1e1eb5bed906a(arg0, arg1, arg2) {
    const ret = arg0.set(arg1, arg2);
    return ret;
}
export function __wbg_set_6be42768c690e380(arg0, arg1, arg2) {
    arg0[arg1] = arg2;
}
export function __wbg_set_6e30c9374c26414c() { return handleError(function (arg0, arg1, arg2) {
    const ret = Reflect.set(arg0, arg1, arg2);
    return ret;
}, arguments); }
export function __wbg_set_body_36614c7e61546809(arg0, arg1) {
    arg0.body = arg1;
}
export function __wbg_set_capture_470b54e0a5c98d83(arg0, arg1) {
    arg0.capture = arg1 !== 0;
}
export function __wbg_set_create_7f95adb9ce7dde2a(arg0, arg1) {
    arg0.create = arg1 !== 0;
}
export function __wbg_set_create_99030ba72d3faa4e(arg0, arg1) {
    arg0.create = arg1 !== 0;
}
export function __wbg_set_dca99999bba88a9a(arg0, arg1, arg2) {
    arg0[arg1 >>> 0] = arg2;
}
export function __wbg_set_index_73ced2e1b4a2a37e(arg0, arg1, arg2) {
    arg0[arg1 >>> 0] = arg2;
}
export function __wbg_set_keep_existing_data_3f5c76813ab5e1ba(arg0, arg1) {
    arg0.keepExistingData = arg1 !== 0;
}
export function __wbg_set_method_7a6811dec7a4feff(arg0, arg1, arg2) {
    arg0.method = getStringFromWasm0(arg1, arg2);
}
export function __wbg_set_mode_c90e3667002857d4(arg0, arg1) {
    arg0.mode = __wbindgen_enum_RequestMode[arg1];
}
export function __wbg_set_once_27df5233613a51cd(arg0, arg1) {
    arg0.once = arg1 !== 0;
}
export function __wbg_set_passive_cd1f03dae3bdd0f9(arg0, arg1) {
    arg0.passive = arg1 !== 0;
}
export function __wbg_set_recursive_8ba27b0bc5f813e8(arg0, arg1) {
    arg0.recursive = arg1 !== 0;
}
export function __wbg_set_signal_d9da62b3f215c821(arg0, arg1) {
    arg0.signal = arg1;
}
export function __wbg_signal_e03304a84df9ed09(arg0) {
    const ret = arg0.signal;
    return ret;
}
export function __wbg_size_9970092b88b1094c(arg0) {
    const ret = arg0.size;
    return ret;
}
export function __wbg_stack_3b0d974bbf31e44f(arg0, arg1) {
    const ret = arg1.stack;
    const ptr1 = passStringToWasm0(ret, wasm.__wbindgen_malloc, wasm.__wbindgen_realloc);
    const len1 = WASM_VECTOR_LEN;
    getDataViewMemory0().setInt32(arg0 + 4 * 1, len1, true);
    getDataViewMemory0().setInt32(arg0 + 4 * 0, ptr1, true);
}
export function __wbg_static_accessor_GLOBAL_9d53f2689e622ca1() {
    const ret = typeof global === 'undefined' ? null : global;
    return isLikeNone(ret) ? 0 : addToExternrefTable0(ret);
}
export function __wbg_static_accessor_GLOBAL_THIS_a1a35cec07001a8a() {
    const ret = typeof globalThis === 'undefined' ? null : globalThis;
    return isLikeNone(ret) ? 0 : addToExternrefTable0(ret);
}
export function __wbg_static_accessor_SELF_4c59f6c7ea29a144() {
    const ret = typeof self === 'undefined' ? null : self;
    return isLikeNone(ret) ? 0 : addToExternrefTable0(ret);
}
export function __wbg_static_accessor_WINDOW_e70ae9f2eb052253() {
    const ret = typeof window === 'undefined' ? null : window;
    return isLikeNone(ret) ? 0 : addToExternrefTable0(ret);
}
export function __wbg_status_00549d55b78d949e(arg0) {
    const ret = arg0.status;
    return ret;
}
export function __wbg_storage_3f560ba25b19525f(arg0) {
    const ret = arg0.storage;
    return ret;
}
export function __wbg_text_a17febec76d36501() { return handleError(function (arg0) {
    const ret = arg0.text();
    return ret;
}, arguments); }
export function __wbg_then_18f476d590e58992(arg0, arg1, arg2) {
    const ret = arg0.then(arg1, arg2);
    return ret;
}
export function __wbg_then_ac7b025999b52837(arg0, arg1) {
    const ret = arg0.then(arg1);
    return ret;
}
export function __wbg_truncate_9922676e99063a88() { return handleError(function (arg0, arg1) {
    const ret = arg0.truncate(arg1 >>> 0);
    return ret;
}, arguments); }
export function __wbg_value_f3625092ee4b37f4(arg0) {
    const ret = arg0.value;
    return ret;
}
export function __wbg_warn_c49a7a9581bf8bea(arg0, arg1, arg2, arg3) {
    console.warn(arg0, arg1, arg2, arg3);
}
export function __wbg_weapon_new(arg0) {
    const ret = Weapon.__wrap(arg0);
    return ret;
}
export function __wbg_write_d396c20c2e70d187() { return handleError(function (arg0, arg1) {
    const ret = arg0.write(arg1);
    return ret;
}, arguments); }
export function __wbindgen_cast_0000000000000001(arg0, arg1) {
    // Cast intrinsic for `Closure(Closure { owned: true, function: Function { arguments: [Externref], shim_idx: 1405, ret: Unit, inner_ret: Some(Unit) }, mutable: true }) -> Externref`.
    const ret = makeMutClosure(arg0, arg1, wasm_bindgen__convert__closures_____invoke__hc188a81508f84c67);
    return ret;
}
export function __wbindgen_cast_0000000000000002(arg0, arg1) {
    // Cast intrinsic for `Closure(Closure { owned: true, function: Function { arguments: [Externref], shim_idx: 1490, ret: Result(Unit), inner_ret: Some(Result(Unit)) }, mutable: true }) -> Externref`.
    const ret = makeMutClosure(arg0, arg1, wasm_bindgen__convert__closures_____invoke__h1520a0cf0b2aae40);
    return ret;
}
export function __wbindgen_cast_0000000000000003(arg0, arg1) {
    // Cast intrinsic for `Closure(Closure { owned: true, function: Function { arguments: [Externref], shim_idx: 963, ret: Unit, inner_ret: Some(Unit) }, mutable: true }) -> Externref`.
    const ret = makeMutClosure(arg0, arg1, wasm_bindgen__convert__closures_____invoke__hd4a1e6a1385cb3fd);
    return ret;
}
export function __wbindgen_cast_0000000000000004(arg0, arg1) {
    // Cast intrinsic for `Closure(Closure { owned: true, function: Function { arguments: [Externref], shim_idx: 964, ret: NamedExternref("Promise<any>"), inner_ret: Some(NamedExternref("Promise<any>")) }, mutable: true }) -> Externref`.
    const ret = makeMutClosure(arg0, arg1, wasm_bindgen__convert__closures_____invoke__ha84f1c1896c592d2);
    return ret;
}
export function __wbindgen_cast_0000000000000005(arg0, arg1) {
    // Cast intrinsic for `Closure(Closure { owned: true, function: Function { arguments: [NamedExternref("IteratorResult<any>")], shim_idx: 371, ret: Result(Unit), inner_ret: Some(Result(Unit)) }, mutable: true }) -> Externref`.
    const ret = makeMutClosure(arg0, arg1, wasm_bindgen__convert__closures_____invoke__hab283fc607567c16);
    return ret;
}
export function __wbindgen_cast_0000000000000006(arg0, arg1) {
    // Cast intrinsic for `Closure(Closure { owned: true, function: Function { arguments: [Ref(NamedExternref("Event"))], shim_idx: 372, ret: Unit, inner_ret: Some(Unit) }, mutable: true }) -> Externref`.
    const ret = makeMutClosure(arg0, arg1, wasm_bindgen__convert__closures________invoke__hbbfc18f6570f4501);
    return ret;
}
export function __wbindgen_cast_0000000000000007(arg0, arg1) {
    // Cast intrinsic for `Closure(Closure { owned: true, function: Function { arguments: [], shim_idx: 1441, ret: Unit, inner_ret: Some(Unit) }, mutable: true }) -> Externref`.
    const ret = makeMutClosure(arg0, arg1, wasm_bindgen__convert__closures_____invoke__h99d559bad173aa23);
    return ret;
}
export function __wbindgen_cast_0000000000000008(arg0) {
    // Cast intrinsic for `F64 -> Externref`.
    const ret = arg0;
    return ret;
}
export function __wbindgen_cast_0000000000000009(arg0) {
    // Cast intrinsic for `I64 -> Externref`.
    const ret = arg0;
    return ret;
}
export function __wbindgen_cast_000000000000000a(arg0, arg1) {
    // Cast intrinsic for `Ref(String) -> Externref`.
    const ret = getStringFromWasm0(arg0, arg1);
    return ret;
}
export function __wbindgen_cast_000000000000000b(arg0) {
    // Cast intrinsic for `U64 -> Externref`.
    const ret = BigInt.asUintN(64, arg0);
    return ret;
}
export function __wbindgen_init_externref_table() {
    const table = wasm.__wbindgen_externrefs;
    const offset = table.grow(4);
    table.set(0, undefined);
    table.set(offset + 0, undefined);
    table.set(offset + 1, null);
    table.set(offset + 2, true);
    table.set(offset + 3, false);
}
function wasm_bindgen__convert__closures_____invoke__h99d559bad173aa23(arg0, arg1) {
    wasm.wasm_bindgen__convert__closures_____invoke__h99d559bad173aa23(arg0, arg1);
}

function wasm_bindgen__convert__closures_____invoke__hc188a81508f84c67(arg0, arg1, arg2) {
    wasm.wasm_bindgen__convert__closures_____invoke__hc188a81508f84c67(arg0, arg1, arg2);
}

function wasm_bindgen__convert__closures_____invoke__hd4a1e6a1385cb3fd(arg0, arg1, arg2) {
    wasm.wasm_bindgen__convert__closures_____invoke__hd4a1e6a1385cb3fd(arg0, arg1, arg2);
}

function wasm_bindgen__convert__closures________invoke__hbbfc18f6570f4501(arg0, arg1, arg2) {
    wasm.wasm_bindgen__convert__closures________invoke__hbbfc18f6570f4501(arg0, arg1, arg2);
}

function wasm_bindgen__convert__closures_____invoke__ha84f1c1896c592d2(arg0, arg1, arg2) {
    const ret = wasm.wasm_bindgen__convert__closures_____invoke__ha84f1c1896c592d2(arg0, arg1, arg2);
    return ret;
}

function wasm_bindgen__convert__closures_____invoke__h1520a0cf0b2aae40(arg0, arg1, arg2) {
    const ret = wasm.wasm_bindgen__convert__closures_____invoke__h1520a0cf0b2aae40(arg0, arg1, arg2);
    if (ret[1]) {
        throw takeFromExternrefTable0(ret[0]);
    }
}

function wasm_bindgen__convert__closures_____invoke__hab283fc607567c16(arg0, arg1, arg2) {
    const ret = wasm.wasm_bindgen__convert__closures_____invoke__hab283fc607567c16(arg0, arg1, arg2);
    if (ret[1]) {
        throw takeFromExternrefTable0(ret[0]);
    }
}

function wasm_bindgen__convert__closures_____invoke__h0a7cf1e297598b08(arg0, arg1, arg2, arg3) {
    wasm.wasm_bindgen__convert__closures_____invoke__h0a7cf1e297598b08(arg0, arg1, arg2, arg3);
}


const __wbindgen_enum_RequestMode = ["same-origin", "no-cors", "cors", "navigate"];
const AnkiDeckPlannerFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_ankideckplanner_free(ptr, 1));
const AudioResultFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_audioresult_free(ptr, 1));
const ClipResultFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_clipresult_free(ptr, 1));
const DeckFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_deck_free(ptr, 1));
const DeckSimulationFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_decksimulation_free(ptr, 1));
const DictionaryWordFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_dictionaryword_free(ptr, 1));
const ListenerKeyFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_listenerkey_free(ptr, 1));
const LockupOfferFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_lockupoffer_free(ptr, 1));
const ReleaseOfferFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_releaseoffer_free(ptr, 1));
const ReviewInfoFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_reviewinfo_free(ptr, 1));
const WeaponFinalization = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(ptr => wasm.__wbg_weapon_free(ptr, 1));

function addToExternrefTable0(obj) {
    const idx = wasm.__externref_table_alloc();
    wasm.__wbindgen_externrefs.set(idx, obj);
    return idx;
}

function _assertClass(instance, klass) {
    if (!(instance instanceof klass)) {
        throw new Error(`expected instance of ${klass.name}`);
    }
}

const CLOSURE_DTORS = (typeof FinalizationRegistry === 'undefined')
    ? { register: () => {}, unregister: () => {} }
    : new FinalizationRegistry(state => wasm.__wbindgen_destroy_closure(state.a, state.b));

function debugString(val) {
    // primitive types
    const type = typeof val;
    if (type == 'number' || type == 'boolean' || val == null) {
        return  `${val}`;
    }
    if (type == 'string') {
        return `"${val}"`;
    }
    if (type == 'symbol') {
        const description = val.description;
        if (description == null) {
            return 'Symbol';
        } else {
            return `Symbol(${description})`;
        }
    }
    if (type == 'function') {
        const name = val.name;
        if (typeof name == 'string' && name.length > 0) {
            return `Function(${name})`;
        } else {
            return 'Function';
        }
    }
    // objects
    if (Array.isArray(val)) {
        const length = val.length;
        let debug = '[';
        if (length > 0) {
            debug += debugString(val[0]);
        }
        for(let i = 1; i < length; i++) {
            debug += ', ' + debugString(val[i]);
        }
        debug += ']';
        return debug;
    }
    // Test for built-in
    const builtInMatches = /\[object ([^\]]+)\]/.exec(toString.call(val));
    let className;
    if (builtInMatches && builtInMatches.length > 1) {
        className = builtInMatches[1];
    } else {
        // Failed to match the standard '[object ClassName]'
        return toString.call(val);
    }
    if (className == 'Object') {
        // we're a user defined class or Object
        // JSON.stringify avoids problems with cycles, and is generally much
        // easier than looping through ownProperties of `val`.
        try {
            return 'Object(' + JSON.stringify(val) + ')';
        } catch (_) {
            return 'Object';
        }
    }
    // errors
    if (val instanceof Error) {
        return `${val.name}: ${val.message}\n${val.stack}`;
    }
    // TODO we could test for more things here, like `Set`s and `Map`s.
    return className;
}

function getArrayU8FromWasm0(ptr, len) {
    ptr = ptr >>> 0;
    return getUint8ArrayMemory0().subarray(ptr / 1, ptr / 1 + len);
}

let cachedDataViewMemory0 = null;
function getDataViewMemory0() {
    if (cachedDataViewMemory0 === null || cachedDataViewMemory0.buffer.detached === true || (cachedDataViewMemory0.buffer.detached === undefined && cachedDataViewMemory0.buffer !== wasm.memory.buffer)) {
        cachedDataViewMemory0 = new DataView(wasm.memory.buffer);
    }
    return cachedDataViewMemory0;
}

function getStringFromWasm0(ptr, len) {
    return decodeText(ptr >>> 0, len);
}

let cachedUint8ArrayMemory0 = null;
function getUint8ArrayMemory0() {
    if (cachedUint8ArrayMemory0 === null || cachedUint8ArrayMemory0.byteLength === 0) {
        cachedUint8ArrayMemory0 = new Uint8Array(wasm.memory.buffer);
    }
    return cachedUint8ArrayMemory0;
}

function handleError(f, args) {
    try {
        return f.apply(this, args);
    } catch (e) {
        const idx = addToExternrefTable0(e);
        wasm.__wbindgen_exn_store(idx);
    }
}

function isLikeNone(x) {
    return x === undefined || x === null;
}

function makeMutClosure(arg0, arg1, f) {
    const state = { a: arg0, b: arg1, cnt: 1 };
    const real = (...args) => {

        // First up with a closure we increment the internal reference
        // count. This ensures that the Rust closure environment won't
        // be deallocated while we're invoking it.
        state.cnt++;
        const a = state.a;
        state.a = 0;
        try {
            return f(a, state.b, ...args);
        } finally {
            state.a = a;
            real._wbg_cb_unref();
        }
    };
    real._wbg_cb_unref = () => {
        if (--state.cnt === 0) {
            wasm.__wbindgen_destroy_closure(state.a, state.b);
            state.a = 0;
            CLOSURE_DTORS.unregister(state);
        }
    };
    CLOSURE_DTORS.register(real, state, state);
    return real;
}

function passStringToWasm0(arg, malloc, realloc) {
    if (realloc === undefined) {
        const buf = cachedTextEncoder.encode(arg);
        const ptr = malloc(buf.length, 1) >>> 0;
        getUint8ArrayMemory0().subarray(ptr, ptr + buf.length).set(buf);
        WASM_VECTOR_LEN = buf.length;
        return ptr;
    }

    let len = arg.length;
    let ptr = malloc(len, 1) >>> 0;

    const mem = getUint8ArrayMemory0();

    let offset = 0;

    for (; offset < len; offset++) {
        const code = arg.charCodeAt(offset);
        if (code > 0x7F) break;
        mem[ptr + offset] = code;
    }
    if (offset !== len) {
        if (offset !== 0) {
            arg = arg.slice(offset);
        }
        ptr = realloc(ptr, len, len = offset + arg.length * 3, 1) >>> 0;
        const view = getUint8ArrayMemory0().subarray(ptr + offset, ptr + len);
        const ret = cachedTextEncoder.encodeInto(arg, view);

        offset += ret.written;
        ptr = realloc(ptr, len, offset, 1) >>> 0;
    }

    WASM_VECTOR_LEN = offset;
    return ptr;
}

function takeFromExternrefTable0(idx) {
    const value = wasm.__wbindgen_externrefs.get(idx);
    wasm.__externref_table_dealloc(idx);
    return value;
}

let cachedTextDecoder = new TextDecoder('utf-8', { ignoreBOM: true, fatal: true });
cachedTextDecoder.decode();
const MAX_SAFARI_DECODE_BYTES = 2146435072;
let numBytesDecoded = 0;
function decodeText(ptr, len) {
    numBytesDecoded += len;
    if (numBytesDecoded >= MAX_SAFARI_DECODE_BYTES) {
        cachedTextDecoder = new TextDecoder('utf-8', { ignoreBOM: true, fatal: true });
        cachedTextDecoder.decode();
        numBytesDecoded = len;
    }
    return cachedTextDecoder.decode(getUint8ArrayMemory0().subarray(ptr, ptr + len));
}

const cachedTextEncoder = new TextEncoder();

if (!('encodeInto' in cachedTextEncoder)) {
    cachedTextEncoder.encodeInto = function (arg, view) {
        const buf = cachedTextEncoder.encode(arg);
        view.set(buf);
        return {
            read: arg.length,
            written: buf.length
        };
    };
}

let WASM_VECTOR_LEN = 0;


let wasm;
export function __wbg_set_wasm(val) {
    wasm = val;
}
